import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";

import EventService from "service/event/EventService";
import RejectConfirmDialog from "./RejectConfirmDialog";
import DrugScheduleEditor from "../drugSchedule/DrugScheduleEditor";
import {
  DS_V2_EDIT_CAPTURED, DS_V2_SHEET_COUNT,
} from "../drugSchedule/events";
import { stagedAddToPlan as dsStagedAddToPlan } from "../drugSchedule/helpers";
import { bodyToCapturedAdd as drugScheduleBodyToCapturedAdd } from "./drugScheduleBodyAdapters";
import { VAS_BANDS } from "../vasScale";
import { SECTION_LABEL, formatStamp } from "./ReviewSummary";
import ComplaintForm from "../chiefComplaints/ComplaintForm";
import DiagnosisForm from "../diagnosis/DiagnosisForm";
import MedicineForm from "../medicines/MedicineForm";
import { prettySalt } from "../medicines/saltAllergyCache";
import { useSaltAllergyCheck } from "./medicineSaltAllergyCheck";
import HospitalisationForm from "../hospitalisation/HospitalisationForm";
import LabInvestigationForm from "../labInvestigations/LabInvestigationForm";
import DigitiseManuallyCard from "../labInvestigations/DigitiseManuallyCard";
import AllergyForm from "../allergies/AllergyForm";
import RelativeForm from "../familyHistory/RelativeForm";
import AddictionForm from "../addictions/AddictionForm";
import { addictionBand, formatDuration, isZeroDuration } from "../addictions/data";
import ActivityForm from "../physicalActivity/ActivityForm";
import { activityIntensityBand, activityStatusBand } from "../physicalActivity/data";
import MetaTooltip from "../medicines/MetaTooltip";
import AnthropometricForm from "../anthropometric/AnthropometricForm";
import { PARAMETER_BY_KEY } from "../anthropometric/data";
import ScheduleEntryForm from "../dailySchedule/ScheduleEntryForm";
import { formatTimeAmPm } from "../dailySchedule/data";
import FoodPreferenceForm from "../foodPreference/FoodPreferenceForm";
import {
  bodyToCapturedAdd,
  bodyToCapturedUpdate,
} from "./complaintBodyAdapters";
import {
  bodyToCapturedAdd as dxBodyToCapturedAdd,
  bodyToCapturedUpdate as dxBodyToCapturedUpdate,
} from "./diagnosisBodyAdapters";
import {
  bodyToCapturedAdd as medBodyToCapturedAdd,
} from "./medicineBodyAdapters";
import {
  bodyToCapturedAdd as hospBodyToCapturedAdd,
  bodyToCapturedUpdate as hospBodyToCapturedUpdate,
} from "./hospitalisationBodyAdapters";
import {
  bodyToCapturedAdd as labBodyToCapturedAdd,
  capturedItemsToCardEditing as labCapturedItemsToCardEditing,
  commitToPrimaryCapturedAdd as labCommitToPrimaryCapturedAdd,
} from "./labInvestigationBodyAdapters";
import {
  bodyToCapturedAdd as algBodyToCapturedAdd,
  bodyToCapturedUpdate as algBodyToCapturedUpdate,
} from "./allergyBodyAdapters";
import {
  bodyToCapturedAdd as fhBodyToCapturedAdd,
  bodyToCapturedUpdate as fhBodyToCapturedUpdate,
} from "./familyHistoryBodyAdapters";
import {
  bodyToCapturedAdd as addBodyToCapturedAdd,
} from "./addictionBodyAdapters";
import {
  bodyToCapturedAdd as actBodyToCapturedAdd,
  bodyToCapturedUpdate as actBodyToCapturedUpdate,
} from "./activityBodyAdapters";
import {
  bodyToCapturedAdd as anthBodyToCapturedAdd,
} from "./anthropometricBodyAdapters";
import {
  bodyToCapturedAdd as dsBodyToCapturedAdd,
} from "./dailyScheduleBodyAdapters";
import {
  bodyToCapturedAdd as fpBodyToCapturedAdd,
} from "./foodPreferenceBodyAdapters";
import { formatShortDate } from "../chiefComplaints/dateHelpers";

/**
 * Step 5 — the full-modal Review & save screen. Care manager walks
 * through each section that had captures, checks the entries, and
 * clicks `Save & next` to commit that section and advance to the next.
 * On the last section this button reads `Save & finish`.
 *
 * IMPORTANT: the actual write to the target v2 backend (CC v2, Dx v2,
 * Medicine v2, ...) is Phase 4 — service.SaveSection today just flips
 * each captured item to `reviewed_and_saved` on the transcript. The UI
 * is fully wired so the flow is testable; the target-service fan-out
 * lands when we know each section's payload adapter.
 */

// Left-rail duration label ("12:45"). Derived from startedAt / endedAt.
const durationLabel = (startedAt, endedAt) => {
  if (!startedAt) return "—";
  const start = new Date(startedAt);
  const end   = endedAt ? new Date(endedAt) : new Date();
  const secs  = Math.max(0, Math.floor((end.getTime() - start.getTime()) / 1000));
  const m = Math.floor(secs / 60);
  const s = String(secs % 60).padStart(2, "0");
  return `${m}:${s}`;
};

const speakerCls   = (s) => (s === "care_manager" ? "care" : "patient");
const speakerLabel = (s) => (s === "care_manager" ? "Care manager" : "Patient");

// Per-section table columns. Chief complaints has the full set from the
// spec mock; other sections fall back to a generic Item / Details / …
// layout until they get their own section-specific view.
// Payload shape differs between staged adds and staged updates:
//   add:    { complaint, icd_code, intensity, since_date, description, ... }
//   update: { complaint, icd_code, vas_band, vas_min, since_when, description, ... }
// Look at both fields for status + since so the review table renders
// consistently regardless of origin.
const cellsForChiefComplaint = (item) => {
  const p = (item && item.payload) || {};
  const complaint  = p.complaint || p.value || "(untitled)";
  const sinceISO   = p.since_date || p.since_when || "";
  const sinceLabel = sinceISO ? formatShortDate(sinceISO) : "—";
  return {
    complaint,
    since:  sinceLabel,
    desc:   p.description || "—",
    status: p.intensity || p.vas_band || "—",
  };
};

const StatusChip = ({ intensity, onClick }) => {
  // Map the AI's severity word into the shared VAS scale so we can reuse
  // the vasBand palette. Unknown / missing → grey chip.
  const band = intensity && VAS_BANDS.find(
    (b) => b.key === String(intensity).toLowerCase(),
  );
  const style = band
    ? { background: band.bg, color: band.fg }
    : { background: "#e5e7eb", color: "#6b7280" };
  const label = band ? band.label : (intensity ? String(intensity) : "—");
  const dot = band ? (band.dot || band.fg) : "#94a3b8";
  if (!onClick) {
    return (
      <span className="ait-rev-status" style={style}>
        <span className="ait-rev-status-dot" aria-hidden="true" style={{ background: dot }} />
        <span>{label}</span>
      </span>
    );
  }
  return (
    <button
      type="button"
      className="ait-rev-status ait-rev-status-btn"
      style={style}
      onClick={onClick}
      title="Edit this entry"
    >
      <span className="ait-rev-status-dot" aria-hidden="true" style={{ background: dot }} />
      <span>{label}</span>
      <span className="ait-rev-status-chev" aria-hidden="true">▾</span>
    </button>
  );
};
StatusChip.propTypes = {
  intensity: PropTypes.string,
  onClick:   PropTypes.func,
};

const ChiefComplaintTable = ({ items, editingId, failures, onEdit, onReject }) => {
  const activeCount = items.filter(
    (it) => it && it.state !== "discarded_in_review",
  ).length;
  return (
    <div className="ait-rev-tblcard">
      <header className="ait-rev-tblcard-head">
        <h3 className="ait-rev-tblcard-title">Chief complaints</h3>
        <span className="ait-rev-tblcard-count">
          · {activeCount} {activeCount === 1 ? "entry" : "entries"}
        </span>
      </header>
      <div className="ait-rev-tbl has-reject" role="table">
        <div className="ait-rev-thead" role="row">
          <span role="columnheader">Complaint</span>
          <span role="columnheader">Since when</span>
          <span role="columnheader">Description</span>
          <span role="columnheader">Status</span>
          <span role="columnheader" aria-label="Actions" />
        </div>
        {items.map((it) => {
          const c = cellsForChiefComplaint(it);
          const isEditing = editingId === it.id;
          const failMsg = failures && failures[it.id];
          const isRejected = it.state === "discarded_in_review";
          return (
            <div
              className={
                `ait-rev-trow`
                + (isEditing ? " is-editing" : "")
                + (failMsg ? " is-failed" : "")
                + (isRejected ? " is-rejected" : "")
              }
              role="row"
              key={it.id}
            >
              <span className="ait-rev-tcell" role="cell">
                <strong>{c.complaint}</strong>
                {failMsg ? (
                  <span className="ait-rev-rowfail" title={failMsg}>
                    · Save failed
                  </span>
                ) : null}
              </span>
              <span className="ait-rev-tcell" role="cell">{c.since}</span>
              <span className="ait-rev-tcell" role="cell">{c.desc}</span>
              <span className="ait-rev-tcell" role="cell">
                {isRejected ? (
                  <StatusChip intensity={c.status} />
                ) : (
                  <StatusChip intensity={c.status} onClick={() => onEdit(it)} />
                )}
              </span>
              <span className="ait-rev-tcell ait-rev-tcell-x" role="cell">
                {!isRejected && onReject ? (
                  <button
                    type="button"
                    className="ait-rev-reject-x"
                    onClick={() => onReject(it)}
                    aria-label={`Reject ${c.complaint}`}
                    title="Reject this entry"
                  >
                    ×
                  </button>
                ) : null}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};
ChiefComplaintTable.propTypes = {
  items:     PropTypes.array.isRequired,
  editingId: PropTypes.string,
  failures:  PropTypes.object,
  onEdit:    PropTypes.func.isRequired,
  onReject:  PropTypes.func,
};
ChiefComplaintTable.defaultProps = { editingId: "", failures: {}, onReject: null };

// Per-row cells for the Diagnosis review table. Same pattern as CC —
// the payload can arrive in either add or update shape, so we look at
// both fields for each column.
const cellsForDiagnosis = (item) => {
  const p = (item && item.payload) || {};
  const name    = p.diagnosis || p.name || p.value || "(untitled)";
  const sinceISO = p.since_date || p.since_when || "";
  const since   = sinceISO ? formatShortDate(sinceISO) : "—";
  const status  = p.treatment_status || "—";
  return { name, since, desc: p.description || "—", status };
};

// Treatment-status chip variant for the review table. Distinct from
// StatusChip (VAS-driven) — this one is a plain enum bucket, so a
// tiny local band table keeps it readable.
const DxStatusChip = ({ status, onClick }) => {
  const norm = String(status || "").toLowerCase();
  const band = {
    ongoing:   { bg: "#fef3c7", fg: "#92400e", dot: "#d97706", label: "Ongoing" },
    completed: { bg: "#dcfce7", fg: "#166534", dot: "#16a34a", label: "Resolved" },
    scheduled: { bg: "#e0e7ff", fg: "#3730a3", dot: "#6366f1", label: "Scheduled" },
    none:      { bg: "#e5e7eb", fg: "#6b7280", dot: "#94a3b8", label: "None" },
  }[norm] || { bg: "#e5e7eb", fg: "#6b7280", dot: "#94a3b8", label: status || "—" };
  const style = { background: band.bg, color: band.fg };
  if (!onClick) {
    return (
      <span className="ait-rev-status" style={style}>
        <span className="ait-rev-status-dot" aria-hidden="true" style={{ background: band.dot }} />
        <span>{band.label}</span>
      </span>
    );
  }
  return (
    <button
      type="button"
      className="ait-rev-status ait-rev-status-btn"
      style={style}
      onClick={onClick}
      title="Edit this entry"
    >
      <span className="ait-rev-status-dot" aria-hidden="true" style={{ background: band.dot }} />
      <span>{band.label}</span>
      <span className="ait-rev-status-chev" aria-hidden="true">▾</span>
    </button>
  );
};
DxStatusChip.propTypes = { status: PropTypes.string, onClick: PropTypes.func };

const activeReviewCount = (items) =>
  (items || []).filter((it) => it && it.state !== "discarded_in_review").length;

const DiagnosisTable = ({ items, editingId, failures, onEdit, onReject }) => (
  <div className="ait-rev-tblcard">
    <header className="ait-rev-tblcard-head">
      <h3 className="ait-rev-tblcard-title">Diagnosis</h3>
      <span className="ait-rev-tblcard-count">
        · {activeReviewCount(items)} {activeReviewCount(items) === 1 ? "entry" : "entries"}
      </span>
    </header>
    <div className="ait-rev-tbl has-reject" role="table">
      <div className="ait-rev-thead" role="row">
        <span role="columnheader">Diagnosis</span>
        <span role="columnheader">Since when</span>
        <span role="columnheader">Description</span>
        <span role="columnheader">Treatment status</span>
        <span role="columnheader" aria-label="Actions" />
      </div>
      {items.map((it) => {
        const c = cellsForDiagnosis(it);
        const isEditing = editingId === it.id;
        const failMsg = failures && failures[it.id];
        const isRejected = it.state === "discarded_in_review";
        return (
          <div
            className={
              `ait-rev-trow`
              + (isEditing ? " is-editing" : "")
              + (failMsg ? " is-failed" : "")
              + (isRejected ? " is-rejected" : "")
            }
            role="row"
            key={it.id}
          >
            <span className="ait-rev-tcell" role="cell">
              <strong>{c.name}</strong>
              {failMsg ? (
                <span className="ait-rev-rowfail" title={failMsg}>
                  · Save failed
                </span>
              ) : null}
            </span>
            <span className="ait-rev-tcell" role="cell">{c.since}</span>
            <span className="ait-rev-tcell" role="cell">{c.desc}</span>
            <span className="ait-rev-tcell" role="cell">
              {isRejected ? (
                <DxStatusChip status={c.status} />
              ) : (
                <DxStatusChip status={c.status} onClick={() => onEdit(it)} />
              )}
            </span>
            <span className="ait-rev-tcell ait-rev-tcell-x" role="cell">
              {!isRejected && onReject ? (
                <button
                  type="button"
                  className="ait-rev-reject-x"
                  onClick={() => onReject(it)}
                  aria-label={`Reject ${c.name}`}
                  title="Reject this entry"
                >×</button>
              ) : null}
            </span>
          </div>
        );
      })}
    </div>
  </div>
);
DiagnosisTable.propTypes = {
  items:     PropTypes.array.isRequired,
  editingId: PropTypes.string,
  failures:  PropTypes.object,
  onEdit:    PropTypes.func.isRequired,
  onReject:  PropTypes.func,
};
DiagnosisTable.defaultProps = { editingId: "", failures: {}, onReject: null };

// Per-row cells for the Medicines review table. Wave A first-cut: shows
// name / dose / frequency / status. Payload's snake_case fields are
// what the transcript captured items carry (either from the agent's
// add_medication or the manual card's medicineBodyAdapters.bodyToCapturedAdd).
const cellsForMedicine = (item) => {
  const p = (item && item.payload) || {};
  return {
    name:      p.medicine || p.name || p.value || "(untitled)",
    dose:      p.dose_qty || "—",
    frequency: p.frequency || "—",
    status:    p.status || "ongoing",
  };
};

// Medication status chip — 3-state (ongoing/completed/scheduled).
const MedStatusChip = ({ status, onClick }) => {
  const norm = String(status || "").toLowerCase();
  const band = {
    ongoing:   { bg: "#fef3c7", fg: "#92400e", dot: "#d97706", label: "Ongoing" },
    completed: { bg: "#dcfce7", fg: "#166534", dot: "#16a34a", label: "Completed" },
    scheduled: { bg: "#e0e7ff", fg: "#3730a3", dot: "#6366f1", label: "Scheduled" },
  }[norm] || { bg: "#e5e7eb", fg: "#6b7280", dot: "#94a3b8", label: status || "—" };
  const style = { background: band.bg, color: band.fg };
  if (!onClick) {
    return (
      <span className="ait-rev-status" style={style}>
        <span className="ait-rev-status-dot" aria-hidden="true" style={{ background: band.dot }} />
        <span>{band.label}</span>
      </span>
    );
  }
  return (
    <button
      type="button"
      className="ait-rev-status ait-rev-status-btn"
      style={style}
      onClick={onClick}
      title="Edit this entry"
    >
      <span className="ait-rev-status-dot" aria-hidden="true" style={{ background: band.dot }} />
      <span>{band.label}</span>
      <span className="ait-rev-status-chev" aria-hidden="true">▾</span>
    </button>
  );
};
MedStatusChip.propTypes = { status: PropTypes.string, onClick: PropTypes.func };

const MedicineTable = ({ items, editingId, failures, onEdit, onReject, patientId }) => {
  // Reviewer-side salt-allergy guard — mirrors the standalone
  // MedicineForm's Add-time check (which pops AllergyGuardDialog on
  // catalog pick). Blank when patientId is missing or no allergies
  // exist; renders inline warning per-row when a match lands.
  const saltChecks = useSaltAllergyCheck(patientId, items);
  return (
    <div className="ait-rev-tblcard">
      <header className="ait-rev-tblcard-head">
        <h3 className="ait-rev-tblcard-title">Medicines</h3>
        <span className="ait-rev-tblcard-count">
          · {activeReviewCount(items)} {activeReviewCount(items) === 1 ? "entry" : "entries"}
        </span>
      </header>
      <div className="ait-rev-tbl has-reject" role="table">
        <div className="ait-rev-thead" role="row">
          <span role="columnheader">Medicine</span>
          <span role="columnheader">Dose</span>
          <span role="columnheader">Frequency</span>
          <span role="columnheader">Status</span>
          <span role="columnheader" aria-label="Actions" />
        </div>
        {items.map((it) => {
          const c = cellsForMedicine(it);
          const isEditing = editingId === it.id;
          const failMsg = failures && failures[it.id];
          const isRejected = it.state === "discarded_in_review";
          const salt = !isRejected && saltChecks[it.id];
          return (
            <React.Fragment key={it.id}>
              <div
                className={
                  `ait-rev-trow`
                  + (isEditing ? " is-editing" : "")
                  + (failMsg ? " is-failed" : "")
                  + (salt ? " has-salt-alert" : "")
                  + (isRejected ? " is-rejected" : "")
                }
                role="row"
              >
                <span className="ait-rev-tcell" role="cell">
                  <strong>{c.name}</strong>
                  {failMsg ? (
                    <span className="ait-rev-rowfail" title={failMsg}>
                      · Save failed
                    </span>
                  ) : null}
                </span>
                <span className="ait-rev-tcell" role="cell">{c.dose}</span>
                <span className="ait-rev-tcell" role="cell">{c.frequency}</span>
                <span className="ait-rev-tcell" role="cell">
                  {isRejected ? (
                    <MedStatusChip status={c.status} />
                  ) : (
                    <MedStatusChip status={c.status} onClick={() => onEdit(it)} />
                  )}
                </span>
                <span className="ait-rev-tcell ait-rev-tcell-x" role="cell">
                  {!isRejected && onReject ? (
                    <button
                      type="button"
                      className="ait-rev-reject-x"
                      onClick={() => onReject(it)}
                      aria-label={`Reject ${c.name}`}
                      title="Reject this entry"
                    >×</button>
                  ) : null}
                </span>
              </div>
              {salt ? (
                <div
                  className="ait-rev-salt-alert"
                  role="alert"
                  aria-label={`Salt allergy warning for ${c.name}`}
                >
                  <span className="ait-rev-salt-alert-icon" aria-hidden="true">⚠</span>
                  <span className="ait-rev-salt-alert-text">
                    <strong>Salt allergy match:</strong>{" "}
                    Patient is allergic to <em>{prettySalt(salt.matchDetail)}</em>
                    {" — review before saving."}
                  </span>
                </div>
              ) : null}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
};
MedicineTable.propTypes = {
  items:     PropTypes.array.isRequired,
  editingId: PropTypes.string,
  failures:  PropTypes.object,
  onEdit:    PropTypes.func.isRequired,
  onReject:  PropTypes.func,
  patientId: PropTypes.number,
};
MedicineTable.defaultProps = { editingId: "", failures: {}, onReject: null, patientId: 0 };

// Per-row cells for the Hospitalisation review table.
const cellsForHospitalisation = (item) => {
  const p = (item && item.payload) || {};
  return {
    name:      p.name || p.value || "(untitled)",
    date:      p.date_of_surgery ? formatShortDate(p.date_of_surgery) : "—",
    procedure: p.cpt_description || (p.cpt_code ? `CPT ${p.cpt_code}` : "—"),
    status:    p.status || "ongoing",
  };
};

const HospStatusChip = ({ status, onClick }) => {
  // The AI's 3-state status enum (ongoing/completed/scheduled) is what
  // lives on the captured payload. Colours match MedStatusChip so the
  // review flow reads consistently across sections.
  const norm = String(status || "").toLowerCase();
  const band = {
    ongoing:   { bg: "#fef3c7", fg: "#92400e", dot: "#d97706", label: "Ongoing" },
    completed: { bg: "#dcfce7", fg: "#166534", dot: "#16a34a", label: "Discharged" },
    scheduled: { bg: "#e0e7ff", fg: "#3730a3", dot: "#6366f1", label: "Scheduled" },
  }[norm] || { bg: "#e5e7eb", fg: "#6b7280", dot: "#94a3b8", label: status || "—" };
  const style = { background: band.bg, color: band.fg };
  if (!onClick) {
    return (
      <span className="ait-rev-status" style={style}>
        <span className="ait-rev-status-dot" aria-hidden="true" style={{ background: band.dot }} />
        <span>{band.label}</span>
      </span>
    );
  }
  return (
    <button
      type="button"
      className="ait-rev-status ait-rev-status-btn"
      style={style}
      onClick={onClick}
      title="Edit this entry"
    >
      <span className="ait-rev-status-dot" aria-hidden="true" style={{ background: band.dot }} />
      <span>{band.label}</span>
      <span className="ait-rev-status-chev" aria-hidden="true">▾</span>
    </button>
  );
};
HospStatusChip.propTypes = { status: PropTypes.string, onClick: PropTypes.func };

const HospitalisationTable = ({ items, editingId, failures, onEdit , onReject }) => (
  <div className="ait-rev-tblcard">
    <header className="ait-rev-tblcard-head">
      <h3 className="ait-rev-tblcard-title">Hospitalisation history</h3>
      <span className="ait-rev-tblcard-count">
        · {activeReviewCount(items)} {activeReviewCount(items) === 1 ? "entry" : "entries"}
      </span>
    </header>
    <div className="ait-rev-tbl has-reject" role="table">
      <div className="ait-rev-thead" role="row">
        <span role="columnheader">Cause</span>
        <span role="columnheader">Hosp. date</span>
        <span role="columnheader">Procedure</span>
        <span role="columnheader">Status</span>
        <span role="columnheader" aria-label="Actions" />
      </div>
      {items.map((it) => {
        const c = cellsForHospitalisation(it);
        const isEditing = editingId === it.id;
        const failMsg = failures && failures[it.id];
        const isRejected = it.state === "discarded_in_review";
        return (
          <div
            className={`ait-rev-trow${isEditing ? " is-editing" : ""}${failMsg ? " is-failed" : ""}${isRejected ? " is-rejected" : ""}`}
            role="row"
            key={it.id}
          >
            <span className="ait-rev-tcell" role="cell">
              <strong>{c.name}</strong>
              {failMsg ? (
                <span className="ait-rev-rowfail" title={failMsg}>
                  · Save failed
                </span>
              ) : null}
            </span>
            <span className="ait-rev-tcell" role="cell">{c.date}</span>
            <span className="ait-rev-tcell" role="cell">{c.procedure}</span>
            <span className="ait-rev-tcell" role="cell">
              {isRejected ? (
                <HospStatusChip status={c.status} />
              ) : (
                <HospStatusChip status={c.status} onClick={() => onEdit(it)} />
              )}
            </span>
              <span className="ait-rev-tcell ait-rev-tcell-x" role="cell">
                {!isRejected && onReject ? (
                  <button
                    type="button"
                    className="ait-rev-reject-x"
                    onClick={() => onReject(it)}
                    aria-label={`Reject ${c.name || c.complaint || c.substance || "this entry"}`}
                    title="Reject this entry"
                  >×</button>
                ) : null}
              </span>
          </div>
        );
      })}
    </div>
  </div>
);
HospitalisationTable.propTypes = {
  items:     PropTypes.array.isRequired,
  editingId: PropTypes.string,
  failures:  PropTypes.object,
  onEdit:    PropTypes.func.isRequired,
  onReject:  PropTypes.func,
};
HospitalisationTable.defaultProps = { editingId: "", failures: {}, onReject: null };

// Per-row cells for the Lab investigations review table. Mirrors the
// calling-mode LabInvestigations table (Investigation · Latest test
// date · Value · Units · Range) minus the Analysis / Trend column,
// plus a Reject × in the trailing action slot.
const cellsForLab = (item) => {
  const p = (item && item.payload) || {};
  return {
    name:  p.test_name || p.value || "(untitled)",
    panel: p.investigation_panel || "",
    date:  p.test_date ? formatShortDate(p.test_date) : "—",
    value: p.result_value || "",
    unit:  p.result_units || "",
    flag:  String(p.flag || "").toLowerCase(),
    // Reviewer-picked qualifier from the SubTestCard Tag dropdown —
    // rendered as a pill after the test name, same "· TagName" style
    // as the calling-mode table.
    tag:   String(p.tag || "").trim(),
  };
};

// Range column — human label used to fill the "Range" cell. Colour
// band comes from the shared `.li3-c-range.is-<flag>` class set the
// calling-mode table uses.
const labRangeLabel = (flag) => ({
  normal:   "Normal",
  abnormal: "Abnormal",
  high:     "High",
  medium:   "Medium",
  low:      "Low",
}[flag] || "—");

const isFlaggedFlag = (flag) =>
  flag === "high" || flag === "low" || flag === "abnormal";

// Group captured items by their parent panel — mirrors calling-mode's
// `groupByPanel` in LabInvestigations.js so the reviewer sees the same
// visual structure (band header + nested sub-test rows) here as they
// did during the call. Items with no panel float in a `loose` bucket
// at the top of the table.
const groupCapturedByPanel = (items) => {
  const loose = [];
  const buckets = new Map();
  (items || []).forEach((it) => {
    const p = (it && it.payload) || {};
    const key = (p.investigation_panel || "").trim();
    if (!key) {
      loose.push(it);
      return;
    }
    if (!buckets.has(key)) {
      buckets.set(key, { key, panel: key, entries: [] });
    }
    buckets.get(key).entries.push(it);
  });
  return {
    loose,
    groups: Array.from(buckets.values()).map((b) => ({
      ...b,
      flaggedCount: b.entries.filter((it) => {
        const p = (it && it.payload) || {};
        return isFlaggedFlag(String(p.flag || "").toLowerCase());
      }).length,
    })),
  };
};

// One sub-test row — shared between the loose top-of-table list and
// the panel-grouped section. Matches the calling-mode `Row` component
// visually (li3-row grid, li3-c-* cells, is-flagged bg for out-of-
// range readings). Analysis / View-trend column is replaced with the
// reject × button per the review-and-save spec.
const LabSubTestRow = ({ it, isEditing, failMsg, onEdit, onReject }) => {
  const c = cellsForLab(it);
  const isRejected = it.state === "discarded_in_review";
  const flagged = isFlaggedFlag(c.flag);
  return (
    <div
      className={`li3-row${flagged ? " is-flagged" : ""}${isEditing ? " is-editing" : ""}${failMsg ? " is-failed" : ""}${isRejected ? " is-rejected" : ""}`}
      role="row"
      onClick={isRejected ? undefined : () => onEdit(it)}
      style={{ cursor: isRejected ? "default" : "pointer" }}
    >
      <span className="li3-cell li3-c-name" role="cell">
        <strong>{c.name}</strong>
        {c.tag ? <span className="li-tag">{c.tag}</span> : null}
        {failMsg ? (
          <span className="ait-rev-rowfail" title={failMsg}> · Save failed</span>
        ) : null}
      </span>
      <span className="li3-cell li3-c-date" role="cell">{c.date}</span>
      <span className="li3-cell li3-c-result" role="cell">
        {c.flag === "high" && <span className="li3-arr" aria-label="High">↑</span>}
        {c.flag === "low" && <span className="li3-arr" aria-label="Low">↓</span>}
        <span
          className="li3-value-clip li-dm-tt"
          data-tt={c.value ? c.value : undefined}
        >
          {c.value || "—"}
        </span>
      </span>
      <span className="li3-cell li3-c-units" role="cell">{c.unit || "—"}</span>
      <span className={`li3-cell li3-c-range is-${c.flag || "none"}`} role="cell">
        {labRangeLabel(c.flag)}
      </span>
      <span
        className="li3-cell li3-c-analysis ait-rev-tcell-x"
        role="cell"
        onClick={(e) => e.stopPropagation()}
      >
        {!isRejected && onReject ? (
          <button
            type="button"
            className="ait-rev-reject-x"
            onClick={() => onReject(it)}
            aria-label={`Reject ${c.name}`}
            title="Reject this entry"
          >×</button>
        ) : null}
      </span>
    </div>
  );
};
LabSubTestRow.propTypes = {
  it:        PropTypes.object.isRequired,
  isEditing: PropTypes.bool,
  failMsg:   PropTypes.string,
  onEdit:    PropTypes.func.isRequired,
  onReject:  PropTypes.func,
};
LabSubTestRow.defaultProps = { isEditing: false, failMsg: "", onReject: null };

const LabInvestigationTable = ({ items, editingId, failures, onEdit, onReject }) => {
  const { loose, groups } = groupCapturedByPanel(items);
  return (
    <div className="ait-rev-tblcard ait-rev-tblcard-li">
      <header className="ait-rev-tblcard-head">
        <h3 className="ait-rev-tblcard-title">Lab investigations</h3>
        <span className="ait-rev-tblcard-count">
          · {activeReviewCount(items)} {activeReviewCount(items) === 1 ? "entry" : "entries"}
        </span>
      </header>
      <div className="li3-table ait-rev-li-table" role="table">
        <div className="li3-thead" role="row">
          <span role="columnheader">Investigation</span>
          <span role="columnheader">Latest test date</span>
          <span role="columnheader">Value</span>
          <span role="columnheader">Units</span>
          <span role="columnheader">Range</span>
          <span role="columnheader" aria-label="Actions" />
        </div>

        {loose.map((it) => (
          <LabSubTestRow
            key={it.id}
            it={it}
            isEditing={editingId === it.id}
            failMsg={failures && failures[it.id]}
            onEdit={onEdit}
            onReject={onReject}
          />
        ))}

        {groups.map((group) => (
          <React.Fragment key={group.key}>
            <div className="li3-band ait-rev-li-band" role="row">
              <span className="li3-band-name">{group.panel}</span>
              <span className="li3-pill">
                {group.entries.length} {group.entries.length === 1 ? "entry" : "entries"}
              </span>
              {group.flaggedCount > 0 && (
                <span className="li3-pill is-flagged">
                  <span className="li3-pill-dot" aria-hidden="true" />
                  {group.flaggedCount} flagged
                </span>
              )}
            </div>
            {group.entries.map((it) => (
              <LabSubTestRow
                key={it.id}
                it={it}
                isEditing={editingId === it.id}
                failMsg={failures && failures[it.id]}
                onEdit={onEdit}
                onReject={onReject}
              />
            ))}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
};
LabInvestigationTable.propTypes = {
  items:     PropTypes.array.isRequired,
  editingId: PropTypes.string,
  failures:  PropTypes.object,
  onEdit:    PropTypes.func.isRequired,
  onReject:  PropTypes.func,
};
LabInvestigationTable.defaultProps = { editingId: "", failures: {}, onReject: null };

const cellsForAllergy = (item) => {
  const p = (item && item.payload) || {};
  const rxsRaw = p.reactions;
  let rxs = "";
  if (Array.isArray(rxsRaw)) {
    rxs = rxsRaw
      .map((r) => (r && typeof r === "object" ? r.name : String(r || "")))
      .filter(Boolean)
      .join(", ");
  } else if (typeof rxsRaw === "string") {
    rxs = rxsRaw.trim();
  }
  return {
    name:     p.substance || p.name || p.value || "(untitled)",
    category: p.category || "—",
    since:    p.since_date ? formatShortDate(p.since_date) : "—",
    rxs:      rxs || "—",
  };
};

const AllergyTable = ({ items, editingId, failures, onEdit , onReject }) => (
  <div className="ait-rev-tblcard">
    <header className="ait-rev-tblcard-head">
      <h3 className="ait-rev-tblcard-title">Allergies</h3>
      <span className="ait-rev-tblcard-count">
        · {activeReviewCount(items)} {activeReviewCount(items) === 1 ? "entry" : "entries"}
      </span>
    </header>
    <div className="ait-rev-tbl has-reject" role="table">
      <div className="ait-rev-thead" role="row">
        <span role="columnheader">Allergen</span>
        <span role="columnheader">Category</span>
        <span role="columnheader">First noted</span>
        <span role="columnheader">Reactions</span>
        <span role="columnheader" aria-label="Actions" />
      </div>
      {items.map((it) => {
        const c = cellsForAllergy(it);
        const isEditing = editingId === it.id;
        const failMsg = failures && failures[it.id];
        const isRejected = it.state === "discarded_in_review";
        return (
          <div
            className={`ait-rev-trow${isEditing ? " is-editing" : ""}${failMsg ? " is-failed" : ""}${isRejected ? " is-rejected" : ""}`}
            role="row"
            key={it.id}
            onClick={isRejected ? undefined : () => onEdit(it)}
            style={{ cursor: isRejected ? "default" : "pointer" }}
          >
            <span className="ait-rev-tcell" role="cell">
              <strong>{c.name}</strong>
              {failMsg ? (
                <span className="ait-rev-rowfail" title={failMsg}>
                  · Save failed
                </span>
              ) : null}
            </span>
            <span className="ait-rev-tcell" role="cell">{c.category}</span>
            <span className="ait-rev-tcell" role="cell">{c.since}</span>
            <span className="ait-rev-tcell" role="cell">{c.rxs}</span>
          <span className="ait-rev-tcell ait-rev-tcell-x" role="cell" onClick={(e) => e.stopPropagation()}>

            {!isRejected && onReject ? (

              <button

                type="button"

                className="ait-rev-reject-x"

                onClick={() => onReject(it)}

                aria-label="Reject entry"

                title="Reject this entry"

              >×</button>

            ) : null}

          </span>

          </div>
        );
      })}
    </div>
  </div>
);
AllergyTable.propTypes = {
  items:     PropTypes.array.isRequired,
  editingId: PropTypes.string,
  failures:  PropTypes.object,
  onEdit:    PropTypes.func.isRequired,
  onReject:  PropTypes.func,
};
AllergyTable.defaultProps = { editingId: "", failures: {}, onReject: null };

const relationshipDisplay = (p) => {
  const rel = String((p && p.relationship) || "other");
  if (rel === "other") {
    return (p && (p.custom_relation || p.customRelation || "")).toString().trim() || "Other";
  }
  return rel.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
};

const cellsForFamilyHistory = (item) => {
  const p = (item && item.payload) || {};
  const condsRaw = p.conditions;
  let conds = "";
  if (Array.isArray(condsRaw)) {
    conds = condsRaw
      .map((c) => (c && typeof c === "object" ? c.name : String(c || "")))
      .filter(Boolean)
      .join(", ");
  } else if (typeof condsRaw === "string") {
    conds = condsRaw.trim();
  } else if (typeof p.condition === "string") {
    conds = p.condition.trim();
  }
  return {
    relative:    relationshipDisplay(p),
    conditions:  conds || "—",
    description: (p.description || "").toString().trim() || "—",
  };
};

const FamilyHistoryTable = ({ items, editingId, failures, onEdit , onReject }) => (
  <div className="ait-rev-tblcard">
    <header className="ait-rev-tblcard-head">
      <h3 className="ait-rev-tblcard-title">Family history</h3>
      <span className="ait-rev-tblcard-count">
        · {activeReviewCount(items)} {activeReviewCount(items) === 1 ? "entry" : "entries"}
      </span>
    </header>
    <div className="ait-rev-tbl has-reject" role="table">
      <div className="ait-rev-thead" role="row">
        <span role="columnheader">Relationship</span>
        <span role="columnheader">Conditions</span>
        <span role="columnheader">Description</span>
        <span role="columnheader" aria-label="Actions" />
      </div>
      {items.map((it) => {
        const c = cellsForFamilyHistory(it);
        const isEditing = editingId === it.id;
        const failMsg = failures && failures[it.id];
        const isRejected = it.state === "discarded_in_review";
        return (
          <div
            className={`ait-rev-trow${isEditing ? " is-editing" : ""}${failMsg ? " is-failed" : ""}${isRejected ? " is-rejected" : ""}`}
            role="row"
            key={it.id}
            onClick={isRejected ? undefined : () => onEdit(it)}
            style={{ cursor: isRejected ? "default" : "pointer" }}
          >
            <span className="ait-rev-tcell" role="cell">
              <strong>{c.relative}</strong>
              {failMsg ? (
                <span className="ait-rev-rowfail" title={failMsg}>
                  · Save failed
                </span>
              ) : null}
            </span>
            <span className="ait-rev-tcell" role="cell">{c.conditions}</span>
            <span className="ait-rev-tcell" role="cell">{c.description}</span>
          <span className="ait-rev-tcell ait-rev-tcell-x" role="cell" onClick={(e) => e.stopPropagation()}>

            {!isRejected && onReject ? (

              <button

                type="button"

                className="ait-rev-reject-x"

                onClick={() => onReject(it)}

                aria-label="Reject entry"

                title="Reject this entry"

              >×</button>

            ) : null}

          </span>

          </div>
        );
      })}
    </div>
  </div>
);
FamilyHistoryTable.propTypes = {
  items:     PropTypes.array.isRequired,
  editingId: PropTypes.string,
  failures:  PropTypes.object,
  onEdit:    PropTypes.func.isRequired,
  onReject:  PropTypes.func,
};
FamilyHistoryTable.defaultProps = { editingId: "", failures: {}, onReject: null };

// Pull the addiction's derived fields off the captured payload — same
// shape the calling-mode Addictions.js table renders (Addiction ·
// Started when · Frequency · Total interval · Description · Status).
const cellsForAddiction = (item) => {
  const p = (item && item.payload) || {};
  const key = String(p.key || "").toLowerCase();
  const label = key ? key.charAt(0).toUpperCase() + key.slice(1) : "(unknown)";
  const status = p.status || "never";
  const startedWhen = {
    years:  Number(p.started_years  || 0),
    months: Number(p.started_months || 0),
    days:   Number(p.started_days   || 0),
  };
  const totalInterval = {
    years:  Number(p.total_years  || 0),
    months: Number(p.total_months || 0),
    days:   Number(p.total_days   || 0),
  };
  return {
    label,
    status,
    frequency:   p.frequency || "",
    description: p.description || "",
    startedWhen,
    totalInterval,
  };
};

// Review-mode Addictions total-interval fallback — same rule the
// calling-mode helper uses: fall back to Started when when the
// interval is blank (unless status is "never", in which case both
// collapse to em-dash).
const addictionTotalIntervalText = (c) => {
  if (c.status === "never") return "—";
  if (!isZeroDuration(c.totalInterval)) return formatDuration(c.totalInterval, false);
  if (!isZeroDuration(c.startedWhen))   return formatDuration(c.startedWhen, false);
  return "—";
};

// Non-interactive Status chip — same coloured pill the calling-mode
// Addictions row shows, minus the caret + click handler (review mode
// doesn't switch status from the row).
const AddictionStatusChip = ({ status }) => {
  const band = addictionBand(status || "never");
  return (
    <span
      className="ad3-chip"
      style={{ background: band.bg, color: band.fg }}
    >
      <span className="ad3-chip-dot" style={{ background: band.dot }} aria-hidden="true" />
      <span className="ad3-chip-label">{band.label}</span>
    </span>
  );
};
AddictionStatusChip.propTypes = { status: PropTypes.string };

const AddictionDash = ({ children }) =>
  children === "—" ? <span className="ad3-dash">&mdash;</span> : children;
AddictionDash.propTypes = { children: PropTypes.node };

const AddictionTable = ({ items, editingId, failures, onEdit, onReject }) => (
  <div className="ait-rev-tblcard ait-rev-tblcard-ad">
    <header className="ait-rev-tblcard-head">
      <h3 className="ait-rev-tblcard-title">Addictions</h3>
      <span className="ait-rev-tblcard-count">
        · {activeReviewCount(items)} {activeReviewCount(items) === 1 ? "entry" : "entries"}
      </span>
    </header>
    {/* Reuse the calling-mode `.ad3-table` grid + cell classes so the
        visual is byte-for-byte identical. Analysis (View trend) column
        is dropped; a trailing Actions column carries the Reject × in
        its place. */}
    <div className="ad3-table ait-rev-ad-table" role="table">
      <div className="ad3-thead" role="row">
        <span role="columnheader">Addiction</span>
        <span role="columnheader">Started when</span>
        <span role="columnheader">Frequency</span>
        <span role="columnheader">Total interval</span>
        <span role="columnheader">Description</span>
        <span role="columnheader">Status</span>
        <span role="columnheader" aria-label="Actions" />
      </div>
      {items.map((it) => {
        const c = cellsForAddiction(it);
        const isEditing = editingId === it.id;
        const failMsg = failures && failures[it.id];
        const isRejected = it.state === "discarded_in_review";
        return (
          <div
            className={`ad3-row${isEditing ? " is-editing" : ""}${failMsg ? " is-failed" : ""}${isRejected ? " is-rejected" : ""}`}
            role="row"
            key={it.id}
            onClick={isRejected ? undefined : () => onEdit(it)}
            style={{ cursor: isRejected ? "default" : "pointer" }}
          >
            <span className="ad3-cell ad3-c-name" role="cell">
              <strong>{c.label}</strong>
              {failMsg ? (
                <span className="ait-rev-rowfail" title={failMsg}> · Save failed</span>
              ) : null}
            </span>
            <span className="ad3-cell ad3-c-started" role="cell">
              <AddictionDash>
                {c.status === "never" ? "—" : (isZeroDuration(c.startedWhen) ? "—" : formatDuration(c.startedWhen, true))}
              </AddictionDash>
            </span>
            <span className="ad3-cell ad3-c-freq" role="cell">
              <AddictionDash>{c.status === "never" ? "—" : (c.frequency || "—")}</AddictionDash>
            </span>
            <span className="ad3-cell ad3-c-total" role="cell">
              <AddictionDash>{addictionTotalIntervalText(c)}</AddictionDash>
            </span>
            <span className="ad3-cell ad3-c-desc" role="cell">
              <MetaTooltip text={c.description || ""} lines={3} />
            </span>
            <span className="ad3-cell ad3-c-status" role="cell">
              <AddictionStatusChip status={c.status} />
            </span>
            <span
              className="ad3-cell ait-rev-tcell-x"
              role="cell"
              onClick={(e) => e.stopPropagation()}
            >
              {!isRejected && onReject ? (
                <button
                  type="button"
                  className="ait-rev-reject-x"
                  onClick={() => onReject(it)}
                  aria-label={`Reject ${c.label}`}
                  title="Reject this entry"
                >×</button>
              ) : null}
            </span>
          </div>
        );
      })}
    </div>
  </div>
);
AddictionTable.propTypes = {
  items:     PropTypes.array.isRequired,
  editingId: PropTypes.string,
  failures:  PropTypes.object,
  onEdit:    PropTypes.func.isRequired,
  onReject:  PropTypes.func,
};
AddictionTable.defaultProps = { editingId: "", failures: {}, onReject: null };

// Physical-activity cells — mirrors the calling-mode PhysicalActivity
// row (Activity · Days per week · Minutes per session · Intensity ·
// Description · Status). Payload uses snake_case per activityBody-
// Adapters; the fallback covers legacy camelCase captures.
const cellsForActivity = (item) => {
  const p = (item && item.payload) || {};
  const dpw = Number(p.days_per_week != null ? p.days_per_week : p.daysPerWeek) || 0;
  const mps = Number(p.minutes_per_session != null ? p.minutes_per_session : p.minutesPerSession) || 0;
  return {
    name:              p.name || p.value || "(untitled)",
    category:          p.category || "",
    daysPerWeek:       dpw,
    minutesPerSession: mps,
    intensity:         p.intensity || "",
    status:            p.status || "",
    description:       p.description || "",
  };
};

// Non-interactive band chip — matches calling-mode `BandChip` visuals
// (pa3-chip + coloured background + label + dot) without the click
// behaviour. Review mode doesn't switch intensity / status from the row.
const ActivityBandChip = ({ band, kind }) => (
  <span
    className={`pa3-chip pa3-chip-${kind}`}
    style={{ background: band.bg, color: band.fg }}
  >
    <span className="pa3-chip-dot" style={{ background: band.dot }} aria-hidden="true" />
    <span className="pa3-chip-label">{band.label}</span>
  </span>
);
ActivityBandChip.propTypes = {
  band: PropTypes.object.isRequired,
  kind: PropTypes.oneOf(["intensity", "status"]).isRequired,
};

const ActivityTable = ({ items, editingId, failures, onEdit, onReject }) => (
  <div className="ait-rev-tblcard ait-rev-tblcard-pa">
    <header className="ait-rev-tblcard-head">
      <h3 className="ait-rev-tblcard-title">Physical activity</h3>
      <span className="ait-rev-tblcard-count">
        · {activeReviewCount(items)} {activeReviewCount(items) === 1 ? "entry" : "entries"}
      </span>
    </header>
    {/* Reuse the calling-mode `.pa3-table` grid + cell classes so the
        visual is byte-for-byte identical. Analysis (View trend) and
        Actions (Delete) columns from calling mode collapse into a
        single trailing Actions column that carries the Reject ×. */}
    <div className="pa3-table ait-rev-pa-table" role="table">
      <div className="pa3-thead" role="row">
        <span role="columnheader">Activity</span>
        <span role="columnheader">Days per week</span>
        <span role="columnheader">Minutes per session</span>
        <span role="columnheader">Intensity</span>
        <span role="columnheader">Description</span>
        <span role="columnheader">Status</span>
        <span role="columnheader" aria-label="Actions" />
      </div>
      {items.map((it) => {
        const c = cellsForActivity(it);
        const isEditing = editingId === it.id;
        const failMsg = failures && failures[it.id];
        const isRejected = it.state === "discarded_in_review";
        return (
          <div
            className={`pa3-row${isEditing ? " is-editing" : ""}${failMsg ? " is-failed" : ""}${isRejected ? " is-rejected" : ""}`}
            role="row"
            key={it.id}
            onClick={isRejected ? undefined : () => onEdit(it)}
            style={{ cursor: isRejected ? "default" : "pointer" }}
          >
            <span className="pa3-cell pa3-c-name" role="cell">
              <strong>{c.name}</strong>
              {c.category ? <span className="pa3-cat"> &middot; {c.category}</span> : null}
              {failMsg ? (
                <span className="ait-rev-rowfail" title={failMsg}> · Save failed</span>
              ) : null}
            </span>
            <span className="pa3-cell pa3-c-days" role="cell">{c.daysPerWeek || "—"}</span>
            <span className="pa3-cell pa3-c-mins" role="cell">{c.minutesPerSession || "—"}</span>
            <span className="pa3-cell pa3-c-intensity" role="cell">
              {c.intensity
                ? <ActivityBandChip band={activityIntensityBand(c.intensity)} kind="intensity" />
                : <span className="pa3-dash">&mdash;</span>}
            </span>
            <span className="pa3-cell pa3-c-desc" role="cell">
              <MetaTooltip text={c.description || ""} lines={3} />
            </span>
            <span className="pa3-cell pa3-c-status" role="cell">
              {c.status
                ? <ActivityBandChip band={activityStatusBand(c.status)} kind="status" />
                : <span className="pa3-dash">&mdash;</span>}
            </span>
            <span
              className="pa3-cell pa3-c-actions ait-rev-tcell-x"
              role="cell"
              onClick={(e) => e.stopPropagation()}
            >
              {!isRejected && onReject ? (
                <button
                  type="button"
                  className="ait-rev-reject-x"
                  onClick={() => onReject(it)}
                  aria-label={`Reject ${c.name}`}
                  title="Reject this entry"
                >×</button>
              ) : null}
            </span>
          </div>
        );
      })}
    </div>
  </div>
);
ActivityTable.propTypes = {
  items:     PropTypes.array.isRequired,
  editingId: PropTypes.string,
  failures:  PropTypes.object,
  onEdit:    PropTypes.func.isRequired,
  onReject:  PropTypes.func,
};
ActivityTable.defaultProps = { editingId: "", failures: {}, onReject: null };

const cellsForAnthropometric = (item) => {
  const p = (item && item.payload) || {};
  const key = p.parameter || "";
  const meta = PARAMETER_BY_KEY[key];
  const label = meta ? meta.label : (key || "(unknown)");
  const value = p.valueDisplay
    || (p.numericValue != null ? String(p.numericValue) : "")
    || "—";
  return {
    parameter: label,
    value,
    unit:      p.unit || "—",
  };
};

const AnthropometricTable = ({ items, editingId, failures, onEdit , onReject }) => (
  <div className="ait-rev-tblcard">
    <header className="ait-rev-tblcard-head">
      <h3 className="ait-rev-tblcard-title">Anthropometric data</h3>
      <span className="ait-rev-tblcard-count">
        · {items.length} {items.length === 1 ? "measurement" : "measurements"}
      </span>
    </header>
    <div className="ait-rev-tbl has-reject" role="table">
      <div className="ait-rev-thead" role="row">
        <span role="columnheader">Parameter</span>
        <span role="columnheader">Value</span>
        <span role="columnheader">Unit</span>
        <span role="columnheader" aria-label="Actions" />
      </div>
      {items.map((it) => {
        const c = cellsForAnthropometric(it);
        const isEditing = editingId === it.id;
        const failMsg = failures && failures[it.id];
        const isRejected = it.state === "discarded_in_review";
        return (
          <div
            className={`ait-rev-trow${isEditing ? " is-editing" : ""}${failMsg ? " is-failed" : ""}${isRejected ? " is-rejected" : ""}`}
            role="row"
            key={it.id}
            onClick={isRejected ? undefined : () => onEdit(it)}
            style={{ cursor: isRejected ? "default" : "pointer" }}
          >
            <span className="ait-rev-tcell" role="cell">
              <strong>{c.parameter}</strong>
              {failMsg ? (
                <span className="ait-rev-rowfail" title={failMsg}>
                  · Save failed
                </span>
              ) : null}
            </span>
            <span className="ait-rev-tcell" role="cell">{c.value}</span>
            <span className="ait-rev-tcell" role="cell">{c.unit}</span>
          <span className="ait-rev-tcell ait-rev-tcell-x" role="cell" onClick={(e) => e.stopPropagation()}>

            {!isRejected && onReject ? (

              <button

                type="button"

                className="ait-rev-reject-x"

                onClick={() => onReject(it)}

                aria-label="Reject entry"

                title="Reject this entry"

              >×</button>

            ) : null}

          </span>

          </div>
        );
      })}
    </div>
  </div>
);
AnthropometricTable.propTypes = {
  items:     PropTypes.array.isRequired,
  editingId: PropTypes.string,
  failures:  PropTypes.object,
  onEdit:    PropTypes.func.isRequired,
  onReject:  PropTypes.func,
};
AnthropometricTable.defaultProps = { editingId: "", failures: {}, onReject: null };

const cellsForDailySchedule = (item) => {
  const p = (item && item.payload) || {};
  const tm = (typeof p.timeMinutes === "number" ? p.timeMinutes
           : typeof p.time_minutes === "number" ? p.time_minutes
           : null);
  return {
    time:     tm != null ? formatTimeAmPm(tm) : "—",
    activity: (p.activity || p.value || "(untitled)").toString(),
  };
};

const DailyScheduleTable = ({ items, editingId, failures, onEdit , onReject }) => (
  <div className="ait-rev-tblcard">
    <header className="ait-rev-tblcard-head">
      <h3 className="ait-rev-tblcard-title">Daily schedule</h3>
      <span className="ait-rev-tblcard-count">
        · {activeReviewCount(items)} {activeReviewCount(items) === 1 ? "entry" : "entries"}
      </span>
    </header>
    <div className="ait-rev-tbl has-reject" role="table">
      <div className="ait-rev-thead" role="row">
        <span role="columnheader">Time</span>
        <span role="columnheader">Activity</span>
        <span role="columnheader" aria-label="Actions" />
      </div>
      {items.map((it) => {
        const c = cellsForDailySchedule(it);
        const isEditing = editingId === it.id;
        const failMsg = failures && failures[it.id];
        const isRejected = it.state === "discarded_in_review";
        return (
          <div
            className={`ait-rev-trow${isEditing ? " is-editing" : ""}${failMsg ? " is-failed" : ""}${isRejected ? " is-rejected" : ""}`}
            role="row"
            key={it.id}
            onClick={isRejected ? undefined : () => onEdit(it)}
            style={{ cursor: isRejected ? "default" : "pointer" }}
          >
            <span className="ait-rev-tcell" role="cell">
              <strong>{c.time}</strong>
              {failMsg ? (
                <span className="ait-rev-rowfail" title={failMsg}>
                  · Save failed
                </span>
              ) : null}
            </span>
            <span className="ait-rev-tcell" role="cell">{c.activity}</span>
          <span className="ait-rev-tcell ait-rev-tcell-x" role="cell" onClick={(e) => e.stopPropagation()}>

            {!isRejected && onReject ? (

              <button

                type="button"

                className="ait-rev-reject-x"

                onClick={() => onReject(it)}

                aria-label="Reject entry"

                title="Reject this entry"

              >×</button>

            ) : null}

          </span>

          </div>
        );
      })}
    </div>
  </div>
);
DailyScheduleTable.propTypes = {
  items:     PropTypes.array.isRequired,
  editingId: PropTypes.string,
  failures:  PropTypes.object,
  onEdit:    PropTypes.func.isRequired,
  onReject:  PropTypes.func,
};
DailyScheduleTable.defaultProps = { editingId: "", failures: {}, onReject: null };

const cellsForFoodPreference = (item) => {
  const p = (item && item.payload) || {};
  const kind = (p.kind || "likes").toString();
  const ingredient = (p.ingredient || p.name || p.value || "(untitled)").toString();
  const kindLabel = {
    likes:        "Like",
    dislikes:     "Dislike",
    restrictions: "Restriction",
    limitations:  "Limitation",
  }[kind] || kind;
  return { ingredient, kindLabel };
};

const cellsForDrugSchedule = (item) => {
  const p = (item && item.payload) || {};
  const doses = Array.isArray(p.doses) ? p.doses : [];
  const first = doses[0] || {};
  const drug = (
    p.drug_name || first.dosename || p.value || "(untitled)"
  ).toString();
  const num = doses.length || Number(p.num_doses) || 0;
  const firstDate = (first.planneddate || p.start_date || "").toString().slice(0, 10);
  const createdAtISO = item && item.createdAt;
  const fmt = (iso) => {
    if (!iso) return "—";
    const d = new Date(iso);
    if (isNaN(d.getTime())) return String(iso).slice(0, 10) || "—";
    return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
  };
  return {
    drug,
    createdOn:    fmt(createdAtISO),
    createdBy:    (p.createdby || "—").toString(),
    upcomingDose: num > 0 ? `Dose 1 of ${num}` : "—",
    nextDoseDate: fmt(firstDate),
    status:       (p.status || "Active").toString(),
  };
};

const FoodPreferenceTable = ({ items, editingId, failures, onEdit , onReject }) => (
  <div className="ait-rev-tblcard">
    <header className="ait-rev-tblcard-head">
      <h3 className="ait-rev-tblcard-title">Food preferences</h3>
      <span className="ait-rev-tblcard-count">
        · {items.length} {items.length === 1 ? "item" : "items"}
      </span>
    </header>
    <div className="ait-rev-tbl has-reject" role="table">
      <div className="ait-rev-thead" role="row">
        <span role="columnheader">Ingredient</span>
        <span role="columnheader">Classified as</span>
        <span role="columnheader" aria-label="Actions" />
      </div>
      {items.map((it) => {
        const c = cellsForFoodPreference(it);
        const isEditing = editingId === it.id;
        const failMsg = failures && failures[it.id];
        const isRejected = it.state === "discarded_in_review";
        return (
          <div
            className={`ait-rev-trow${isEditing ? " is-editing" : ""}${failMsg ? " is-failed" : ""}${isRejected ? " is-rejected" : ""}`}
            role="row"
            key={it.id}
            onClick={isRejected ? undefined : () => onEdit(it)}
            style={{ cursor: isRejected ? "default" : "pointer" }}
          >
            <span className="ait-rev-tcell" role="cell">
              <strong>{c.ingredient}</strong>
              {failMsg ? (
                <span className="ait-rev-rowfail" title={failMsg}>
                  · Save failed
                </span>
              ) : null}
            </span>
            <span className="ait-rev-tcell" role="cell">{c.kindLabel}</span>
          <span className="ait-rev-tcell ait-rev-tcell-x" role="cell" onClick={(e) => e.stopPropagation()}>

            {!isRejected && onReject ? (

              <button

                type="button"

                className="ait-rev-reject-x"

                onClick={() => onReject(it)}

                aria-label="Reject entry"

                title="Reject this entry"

              >×</button>

            ) : null}

          </span>

          </div>
        );
      })}
    </div>
  </div>
);
FoodPreferenceTable.propTypes = {
  items:     PropTypes.array.isRequired,
  editingId: PropTypes.string,
  failures:  PropTypes.object,
  onEdit:    PropTypes.func.isRequired,
  onReject:  PropTypes.func,
};
FoodPreferenceTable.defaultProps = { editingId: "", failures: {}, onReject: null };

/**
 * Patient details — Review Save table.
 *
 * Single-record UPDATE section: at most ONE captured item ever exists,
 * carrying any subset of firstName / lastName / gender / dateOfBirth /
 * email. Only MODIFIED fields (differ from the current patient record)
 * are rendered — the reviewer isn't asked to confirm fields they
 * never edited.
 *
 * Per-field reject × strikes the row through (client state only). At
 * Save & finish the parent iterates the rejected set and rewrites
 * each item's server payload via decideCaptured BEFORE the
 * SaveSection RPC fires, so the Go writePatientDetails handler
 * commits only the survivors.
 */
const PATIENT_DETAILS_FIELD_LABEL = {
  firstName:   "First name",
  lastName:    "Last name",
  gender:      "Gender",
  dateOfBirth: "Date of birth",
  email:       "Communication email",
};
const patientDetailsDisplay = (key, v) => {
  if (v === null || v === undefined || v === "") return "";
  if (key === "gender") return v === 2 ? "Male" : v === 1 ? "Female" : String(v);
  if (key === "dateOfBirth") {
    const d = new Date(v);
    return isNaN(d.getTime())
      ? String(v)
      : d.toLocaleDateString("en-GB", { day: "2-digit", month: "long", year: "numeric" });
  }
  return String(v);
};
// Two values match if they represent the same content. Handles the
// `dateOfBirth` shape mismatch (payload YYYY-MM-DD vs current
// ISO), and empty-vs-null-vs-undefined equivalence.
const patientDetailsMatches = (key, a, b) => {
  const na = a === null || a === undefined ? "" : a;
  const nb = b === null || b === undefined ? "" : b;
  if (key === "gender") return Number(na || 0) === Number(nb || 0);
  if (key === "dateOfBirth") {
    const norm = (v) => {
      if (!v) return "";
      const d = new Date(v);
      return isNaN(d.getTime()) ? String(v) : d.toISOString().slice(0, 10);
    };
    return norm(na) === norm(nb);
  }
  return String(na) === String(nb);
};
const patientDetailsModifiedRows = (payload, current) => {
  if (!payload) return [];
  const out = [];
  const c = current || {};
  for (const [key, label] of Object.entries(PATIENT_DETAILS_FIELD_LABEL)) {
    if (!(key in payload)) continue;
    const staged = payload[key];
    // The "current" record uses `phoneno` (lowercase o) for one field
    // but every field we compare uses the same casing on both sides.
    const cur = c[key];
    if (patientDetailsMatches(key, staged, cur)) continue;
    const display = patientDetailsDisplay(key, staged);
    if (display === "") continue;
    out.push({ key, label, display });
  }
  return out;
};

const PatientDetailsTable = ({
  items,
  editingId,
  failures,
  onEdit,
  onReject,
  patient,
  rejectedFields,
  onRejectField,
}) => (
  <div className="ait-rev-tblcard">
    <header className="ait-rev-tblcard-head">
      <h3 className="ait-rev-tblcard-title">Patient details</h3>
      <span className="ait-rev-tblcard-count">
        · {activeReviewCount(items)} {activeReviewCount(items) === 1 ? "entry" : "entries"}
      </span>
    </header>
    <div className="pd-rev-table" role="table" aria-label="Patient details staged changes">
      <div className="pd-rev-thead" role="row">
        <span role="columnheader">Field</span>
        <span role="columnheader">Staged value</span>
        <span role="columnheader" aria-label="Actions" />
      </div>
      {items.map((it) => {
        const isEditing = editingId === it.id;
        const failMsg = failures && failures[it.id];
        const isRejected = it.state === "discarded_in_review";
        const rows = patientDetailsModifiedRows(it.payload || {}, patient || {});
        const itemRejects = (rejectedFields && rejectedFields.get(it.id)) || null;
        if (rows.length === 0) {
          return (
            <div
              className={`pd-rev-row${isEditing ? " is-editing" : ""}${failMsg ? " is-failed" : ""}${isRejected ? " is-rejected" : ""}`}
              role="row"
              key={it.id}
              onClick={isRejected ? undefined : () => onEdit(it)}
              style={{ cursor: isRejected ? "default" : "pointer" }}
            >
              <span className="pd-rev-cell pd-rev-c-field" role="cell">—</span>
              <span className="pd-rev-cell pd-rev-c-value" role="cell">
                <em>No modified fields</em>
                {failMsg ? (
                  <span className="ait-rev-rowfail" title={failMsg}> · Save failed</span>
                ) : null}
              </span>
              <span className="pd-rev-cell ait-rev-tcell-x" role="cell" onClick={(e) => e.stopPropagation()}>
                {!isRejected && onReject ? (
                  <button
                    type="button"
                    className="ait-rev-reject-x"
                    onClick={() => onReject(it)}
                    aria-label="Reject patient details update"
                    title="Reject this entry"
                  >×</button>
                ) : null}
              </span>
            </div>
          );
        }
        return rows.map((r, idx) => {
          const isFieldRejected = itemRejects && itemRejects.has(r.key);
          return (
            <div
              className={
                `pd-rev-row` +
                `${isEditing ? " is-editing" : ""}` +
                `${failMsg ? " is-failed" : ""}` +
                `${isRejected ? " is-rejected" : ""}` +
                `${isFieldRejected ? " is-field-rejected" : ""}`
              }
              role="row"
              key={`${it.id}-${r.key}`}
              onClick={(isRejected || isFieldRejected) ? undefined : () => onEdit(it)}
              style={{ cursor: (isRejected || isFieldRejected) ? "default" : "pointer" }}
            >
              <span className="pd-rev-cell pd-rev-c-field" role="cell">{r.label}</span>
              <span className="pd-rev-cell pd-rev-c-value" role="cell">
                <strong>{r.display}</strong>
                {idx === 0 && failMsg ? (
                  <span className="ait-rev-rowfail" title={failMsg}> · Save failed</span>
                ) : null}
              </span>
              <span className="pd-rev-cell ait-rev-tcell-x" role="cell" onClick={(e) => e.stopPropagation()}>
                {/* Per-field reject × — first click strikes the row
                    through (client state only); second click restores.
                    The actual payload mutation happens at Save & finish
                    time so the reviewer can undo without a round trip. */}
                {!isRejected && onRejectField ? (
                  <button
                    type="button"
                    className="ait-rev-reject-x"
                    onClick={() => onRejectField(it, r.key)}
                    aria-label={
                      isFieldRejected
                        ? `Restore ${r.label}`
                        : `Reject ${r.label}`
                    }
                    title={isFieldRejected ? "Restore this field" : "Reject this field"}
                  >×</button>
                ) : null}
              </span>
            </div>
          );
        });
      })}
    </div>
  </div>
);
PatientDetailsTable.propTypes = {
  items:     PropTypes.array.isRequired,
  editingId: PropTypes.string,
  failures:  PropTypes.object,
  onEdit:    PropTypes.func.isRequired,
  onReject:  PropTypes.func,
  patient:   PropTypes.object,
  rejectedFields: PropTypes.instanceOf(Map),
  onRejectField:  PropTypes.func,
};
PatientDetailsTable.defaultProps = {
  editingId: "",
  failures: {},
  onReject: null,
  patient: null,
  rejectedFields: null,
  onRejectField: null,
};

const DrugScheduleTable = ({ items, editingId, failures, onEdit, onReject }) => (
  <div className="ait-rev-tblcard">
    <header className="ait-rev-tblcard-head">
      <h3 className="ait-rev-tblcard-title">Drug schedule</h3>
      <span className="ait-rev-tblcard-count">
        · {items.length} {items.length === 1 ? "plan" : "plans"}
      </span>
    </header>
    {/* is-ds widens the grid to match the calling-mode plans table
        (Drug / Created on / Created by / Upcoming dose / Next dose
        date / Status), plus the reject × column. */}
    <div className="ait-rev-tbl has-reject is-ds" role="table">
      <div className="ait-rev-thead" role="row">
        <span role="columnheader">Drug</span>
        <span role="columnheader">Created on</span>
        <span role="columnheader">Created by</span>
        <span role="columnheader">Upcoming dose</span>
        <span role="columnheader">Next dose date</span>
        <span role="columnheader">Status</span>
        <span role="columnheader" aria-label="Actions" />
      </div>
      {items.map((it) => {
        const c = cellsForDrugSchedule(it);
        const isEditing = editingId === it.id;
        const failMsg = failures && failures[it.id];
        const isRejected = it.state === "discarded_in_review";
        return (
          <div
            className={`ait-rev-trow${isEditing ? " is-editing" : ""}${failMsg ? " is-failed" : ""}${isRejected ? " is-rejected" : ""}`}
            role="row"
            key={it.id}
          >
            {/* Only the DRUG cell is clickable — opens the edit card in
                the right column. Other cells (dates, status, created by)
                stay non-interactive; the × on the far right handles
                reject. Row-wide click was too easy to trip when the
                reviewer meant to hover a date. */}
            <span className="ait-rev-tcell" role="cell">
              {isRejected ? (
                <strong>{c.drug}</strong>
              ) : (
                <button
                  type="button"
                  className="ait-rev-drug-btn"
                  onClick={() => onEdit(it)}
                  title="Edit this schedule"
                >
                  <strong>{c.drug}</strong>
                </button>
              )}
              {failMsg ? (
                <span className="ait-rev-rowfail" title={failMsg}>
                  · Save failed
                </span>
              ) : null}
            </span>
            <span className="ait-rev-tcell" role="cell">{c.createdOn}</span>
            <span className="ait-rev-tcell" role="cell">{c.createdBy}</span>
            <span className="ait-rev-tcell" role="cell">{c.upcomingDose}</span>
            <span className="ait-rev-tcell" role="cell">{c.nextDoseDate}</span>
            <span className="ait-rev-tcell" role="cell">{c.status}</span>
            <span className="ait-rev-tcell ait-rev-tcell-x" role="cell" onClick={(e) => e.stopPropagation()}>
              {!isRejected && onReject ? (
                <button
                  type="button"
                  className="ait-rev-reject-x"
                  onClick={() => onReject(it)}
                  aria-label="Reject entry"
                  title="Reject this entry"
                >×</button>
              ) : null}
            </span>
          </div>
        );
      })}
    </div>
  </div>
);
DrugScheduleTable.propTypes = {
  items:     PropTypes.array.isRequired,
  editingId: PropTypes.string,
  failures:  PropTypes.object,
  onEdit:    PropTypes.func.isRequired,
  onReject:  PropTypes.func,
};
DrugScheduleTable.defaultProps = { editingId: "", failures: {}, onReject: null };

const GenericTable = ({ items, heading }) => (
  <div className="ait-rev-tblcard">
    <header className="ait-rev-tblcard-head">
      <h3 className="ait-rev-tblcard-title">{heading}</h3>
      <span className="ait-rev-tblcard-count">
        · {activeReviewCount(items)} {activeReviewCount(items) === 1 ? "entry" : "entries"}
      </span>
    </header>
    <div className="ait-rev-tbl has-reject" role="table">
      <div className="ait-rev-thead" role="row">
        <span role="columnheader">Item</span>
        <span role="columnheader">Details</span>
        <span role="columnheader">Evidence</span>
        <span role="columnheader" aria-label="Actions" />
      </div>
      {items.map((it) => (
        <div className="ait-rev-trow" role="row" key={it.id}>
          <span className="ait-rev-tcell" role="cell">
            <strong>{(it.payload && (it.payload.name || it.payload.value)) || "(untitled)"}</strong>
          </span>
          <span className="ait-rev-tcell" role="cell">
            {it.payload && it.payload.icd_code ? `ICD ${it.payload.icd_code}` : "—"}
          </span>
          <span className="ait-rev-tcell" role="cell">
            {it.evidence ? `“${it.evidence}”` : "—"}
          </span>
        </div>
      ))}
    </div>
  </div>
);
GenericTable.propTypes = {
  items:   PropTypes.array.isRequired,
  heading: PropTypes.string.isRequired,
};

/**
 * SectionCaptureTable — router that picks the right per-section table
 * for a captured-items list. Exported so past-transcript view in
 * CapturedSections can render the exact same summary the S5 Review
 * screen uses (reviewer sees consistent columns whether they're
 * committing captures or just browsing a reviewed transcript).
 *
 * `editingId` / `failures` / `onEdit` are optional — pass them from
 * the S5 review flow so a click on a row opens the click-to-edit
 * card and the "Save failed" chip renders; the past-transcript view
 * doesn't need them (view-only).
 */
export const SectionCaptureTable = ({
  section,
  items,
  editingId,
  failures,
  onEdit,
  onReject,
  patientId,
}) => {
  const rowProps = {
    items,
    editingId: editingId || "",
    failures:  failures || {},
    onEdit:    onEdit || (() => {}),
    onReject:  onReject || null,
  };
  switch (section) {
    case "chief_complaints":  return <ChiefComplaintTable {...rowProps} />;
    case "diagnosis":         return <DiagnosisTable {...rowProps} />;
    case "medicines":         return <MedicineTable {...rowProps} patientId={patientId || 0} />;
    case "hospitalisation":   return <HospitalisationTable {...rowProps} />;
    case "lab_investigations": return <LabInvestigationTable {...rowProps} />;
    case "allergies":         return <AllergyTable {...rowProps} />;
    case "family_history":    return <FamilyHistoryTable {...rowProps} />;
    case "addictions":        return <AddictionTable {...rowProps} />;
    case "activity":          return <ActivityTable {...rowProps} />;
    case "anthropometric":    return <AnthropometricTable {...rowProps} />;
    case "daily_schedule":    return <DailyScheduleTable {...rowProps} />;
    case "food_preferences":  return <FoodPreferenceTable {...rowProps} />;
    case "drug_schedule":     return <DrugScheduleTable {...rowProps} />;
    case "patient_details":   return <PatientDetailsTable {...rowProps} />;
    default:
      return (
        <GenericTable
          items={items}
          heading={SECTION_LABEL[section] || section || "Section"}
        />
      );
  }
};
SectionCaptureTable.propTypes = {
  section:   PropTypes.string,
  items:     PropTypes.array.isRequired,
  editingId: PropTypes.string,
  failures:  PropTypes.object,
  onEdit:    PropTypes.func,
  /** Reject-entry callback (item → void). When absent, tables render
   *  without the × column. */
  onReject:  PropTypes.func,
  patientId: PropTypes.number,
};
SectionCaptureTable.defaultProps = {
  section:   "",
  editingId: "",
  failures:  {},
  onEdit:    null,
  onReject:  null,
  patientId: 0,
};

/**
 * CapturedItemCard — the click-to-edit card that opens next to the
 * review table when a row's status chip is clicked. Extracted so the
 * past-transcript view in CapturedSections can render the same card
 * (with a view-only save handler) — reviewer sees the exact same
 * layout whether they're staging or browsing.
 *
 * `onSave` receives the transformed captured-shape payload (add or
 * update) — the section-specific body → captured adapter runs
 * inside this component so callers don't need to know which adapter
 * pairs with which section.
 */
export const CapturedItemCard = ({
  captured, section, patientId, appointmentId,
  saving, error, onSave, onClose,
  readOnly,
}) => {
  if (!captured) return null;
  // View-only wrap for past-transcript view. `fieldset[disabled]`
  // natively disables every native form control inside (inputs,
  // selects, textareas, buttons) — React portals are unaffected,
  // but the forms we ship use inline controls only. The ribbon at
  // the top labels the mode so the reviewer isn't hunting for a
  // save button.
  const wrap = (node) => (
    readOnly ? (
      <div className="ait-card-readonly">
        <div className="ait-card-readonly-tag" aria-hidden="true">
          View only — transcript already saved.
          <button
            type="button"
            className="ait-card-readonly-close"
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </div>
        <fieldset disabled className="ait-card-readonly-fs">{node}</fieldset>
      </div>
    ) : node
  );
  if (section === "lab_investigations") {
    // Transcript-mode LI review swaps the flat LabInvestigationForm
    // for a card-shaped DigitiseManuallyCard (same fields, same
    // validation — see the design lock in ../labInvestigations/
    // DigitiseManuallyCard.js). The card is scoped to the ONE
    // (Lab test, Test date) group the reviewer clicked; sub-tests
    // for that group are all seeded in a single card.
    //
    // Wave A first-cut: the card commits back through the existing
    // per-item `onSave` channel — the FIRST sub-test in the commit
    // maps onto the current captured item; additional sub-tests are
    // preserved on the payload's `_extra_sub_tests` field so a Phase-2
    // multi-item fan-out (CapturedSections plumbing) can pick them up
    // without a wire-shape change. Reviewers can already fill / edit
    // multi-sub-test cards visually — the trailing rows just don't
    // land as their own captured items yet.
    const editing = labCapturedItemsToCardEditing([captured]);
    const handleAdd = (commit) => {
      const converted = labCommitToPrimaryCapturedAdd(commit, appointmentId);
      if (converted) onSave(converted);
    };
    return wrap(
      <DigitiseManuallyCard
        key={captured.id}
        formId={captured.id}
        editing={editing}
        saving={saving}
        error={error}
        onAdd={handleAdd}
        onAddAnother={handleAdd}
        onClose={onClose}
        themeClass="is-manual"
      />
    );
  }
  if (section === "hospitalisation") {
    // Staged-update: synthesise `editing` from payload + targetEntryId
    // so the form opens in edit mode and skips the duplicate gate.
    if (captured.operation === "update") {
      const p = captured.payload || {};
      const editing = {
        hospitalisationId: captured.targetEntryId,
        cause: {
          icdCode:     p.icd_code        || "",
          icdName:     p.icd_description || "",
          description: p.name            || "",
        },
        status: p.status === "completed"  ? "discharged"
              : p.status === "ongoing"    ? "hospitalised"
              : p.status === "scheduled"  ? "hospitalised"
              : "hospitalised",
        hospitalisationDate: p.date_of_surgery   || null,
        dischargeDate:       p.date_of_discharge || null,
        procedures:          p.cpt_code
          ? [{ code: p.cpt_code, name: p.cpt_description || "", source: "catalog" }]
          : [],
        description:         p.description || "",
      };
      return wrap(
        <HospitalisationForm
          key={captured.id}
          formId={captured.id}
          patientId={patientId}
          editing={editing}
          saving={saving}
          error={error}
          onSave={(body) => onSave(hospBodyToCapturedUpdate(body, { record: editing }))}
          onClose={onClose}
          themeClass="is-update"
          lockName
        />
      );
    }
    return wrap(
      <HospitalisationForm
        key={captured.id}
        formId={captured.id}
        patientId={patientId}
        initialPayload={captured.payload}
        saving={saving}
        error={error}
        onSave={(body) => onSave(hospBodyToCapturedAdd(body))}
        onClose={onClose}
        themeClass="is-manual"
        lockName
      />
    );
  }
  if (section === "allergies") {
    if (captured.operation === "update") {
      const p = captured.payload || {};
      const editing = {
        allergyId:   captured.targetEntryId,
        name:        p.name || p.substance || "",
        category:    p.category || "Other",
        reactions:   Array.isArray(p.reactions) ? p.reactions : [],
        firstNoted:  p.since_date || "",
        description: p.description || "",
      };
      return wrap(
        <AllergyForm
          key={captured.id}
          formId={captured.id}
          patientId={patientId}
          editing={editing}
          saving={saving}
          error={error}
          onSave={(body) => onSave(algBodyToCapturedUpdate(body, { record: editing }))}
          onClose={onClose}
          themeClass="is-update"
          lockName
        />
      );
    }
    return wrap(
      <AllergyForm
        key={captured.id}
        formId={captured.id}
        patientId={patientId}
        initialPayload={captured.payload}
        saving={saving}
        error={error}
        onSave={(body) => onSave(algBodyToCapturedAdd(body))}
        onClose={onClose}
        themeClass="is-manual"
        lockName
      />
    );
  }
  if (section === "family_history") {
    if (captured.operation === "update") {
      const p = captured.payload || {};
      const editing = {
        relativeId:     captured.targetEntryId,
        relationship:   p.relationship   || "other",
        customRelation: p.custom_relation || "",
        conditions:     Array.isArray(p.conditions) ? p.conditions : [],
        description:    p.description    || "",
      };
      return wrap(
        <RelativeForm
          key={captured.id}
          formId={captured.id}
          patientId={patientId}
          editing={editing}
          saving={saving}
          error={error}
          onSave={(body) => onSave(fhBodyToCapturedUpdate(body, { record: editing }))}
          onClose={onClose}
          themeClass="is-update"
          lockName
          menuClassName="ait-menu"
        />
      );
    }
    return wrap(
      <RelativeForm
        key={captured.id}
        formId={captured.id}
        patientId={patientId}
        initialPayload={captured.payload}
        saving={saving}
        error={error}
        onSave={(body) => onSave(fhBodyToCapturedAdd(body))}
        onClose={onClose}
        themeClass="is-manual"
        lockName
        menuClassName="ait-menu"
      />
    );
  }
  if (section === "food_preferences") {
    return wrap(
      <FoodPreferenceForm
        key={captured.id}
        formId={captured.id}
        initialPayload={captured.payload}
        saving={saving}
        error={error}
        onSave={(body) => onSave(fpBodyToCapturedAdd(body))}
        onClose={onClose}
        themeClass="is-manual"
        lockName
      />
    );
  }
  if (section === "daily_schedule") {
    return wrap(
      <ScheduleEntryForm
        key={captured.id}
        formId={captured.id}
        patientId={patientId}
        initialPayload={captured.payload}
        saving={saving}
        error={error}
        onSave={(body) => onSave(dsBodyToCapturedAdd(body))}
        onClose={onClose}
        themeClass="is-manual"
        lockName
      />
    );
  }
  if (section === "anthropometric") {
    return wrap(
      <AnthropometricForm
        key={captured.id}
        formId={captured.id}
        patientId={patientId}
        initialPayload={captured.payload}
        saving={saving}
        error={error}
        onSave={(body) => onSave(anthBodyToCapturedAdd(body))}
        onClose={onClose}
        themeClass="is-manual"
      />
    );
  }
  if (section === "activity") {
    // Staged-update: synthesise `editing` from the payload + targetEntryId
    // so isEdit=true inside ActivityForm (skips the duplicate-name gate
    // that otherwise fires when the row's own name looks like a "new
    // entry" collision on re-open). Mirrors Dx's pattern above.
    if (captured.operation === "update") {
      const p = captured.payload || {};
      const editing = {
        activityId:        captured.targetEntryId,
        name:              p.name        || "",
        category:          p.category    || "",
        catalogId:         p.catalog_id  || "",
        daysPerWeek:       p.days_per_week != null      ? p.days_per_week      : 0,
        minutesPerSession: p.minutes_per_session != null ? p.minutes_per_session : 0,
        intensity:         p.intensity   || "moderate",
        status:            p.status      || "active",
        description:       p.description || "",
      };
      return wrap(
        <ActivityForm
          key={captured.id}
          formId={captured.id}
          patientId={patientId}
          editing={editing}
          saving={saving}
          error={error}
          onSave={(body) => onSave(actBodyToCapturedUpdate(body, { record: editing }))}
          onClose={onClose}
          themeClass="is-update"
          menuClassName="ait-menu"
        />
      );
    }
    return wrap(
      <ActivityForm
        key={captured.id}
        formId={captured.id}
        patientId={patientId}
        initialPayload={captured.payload}
        saving={saving}
        error={error}
        onSave={(body) => onSave(actBodyToCapturedAdd(body))}
        onClose={onClose}
        themeClass="is-manual"
        /* No `lockName` here: fresh AI proposal review still needs the
           reviewer to pick the activity from the catalog. Locking the
           name would bypass ActivityForm's catalogPicked gate and let
           free-text entries save. */
        menuClassName="ait-menu"
      />
    );
  }
  if (section === "addictions") {
    const k = captured.payload && captured.payload.key;
    const seedEditing = k ? { key: k, label: k.charAt(0).toUpperCase() + k.slice(1) } : {};
    return wrap(
      <AddictionForm
        key={captured.id}
        formId={captured.id}
        patientId={patientId}
        editing={seedEditing}
        initialPayload={captured.payload}
        saving={saving}
        error={error}
        onSave={(body) => onSave(addBodyToCapturedAdd(body))}
        onClose={onClose}
        themeClass="is-manual"
        menuClassName="ait-menu"
      />
    );
  }
  if (section === "medicines") {
    // Medicine name is no longer locked in transcript mode. The AI's
    // proposed name seeds the autocomplete input; the picker's mount-
    // time prefetch (MedicineCatalogAutocomplete lines 134-167) fires
    // an initial search and auto-picks when the seed matches a catalog
    // row exactly (case-insensitive) — so the reviewer never has to
    // click for a clean AI hit. When the seed is fuzzy or misheard,
    // the dropdown stays open and the reviewer clicks a real row. The
    // form's canSubmit now requires a catalog binding (mCode) so Save
    // is greyed out until the picker resolves either way — no free-
    // text medicines can leave this surface.
    return wrap(
      <MedicineForm
        key={captured.id}
        formId={captured.id}
        patientId={patientId}
        initialPayload={captured.payload}
        saving={saving}
        error={error}
        onSave={(body) => onSave(medBodyToCapturedAdd(body))}
        onClose={onClose}
        themeClass="is-manual"
      />
    );
  }
  if (section === "diagnosis") {
    if (captured.operation === "update") {
      const p = captured.payload || {};
      const editing = {
        diagnosis:     p.diagnosis || "",
        diagnosisCode: p.icd_code ? { icdCode: p.icd_code } : null,
        diagnosisId:   captured.targetEntryId,
        ongoing: p.treatment_status
          ? String(p.treatment_status).toLowerCase() === "ongoing"
          : true,
        current: {
          sinceWhen:       p.since_when || "",
          description:     p.description || "",
          consultedDoctor: !!p.consulted_doctor,
        },
      };
      return wrap(
        <DiagnosisForm
          key={captured.id}
          formId={captured.id}
          editing={editing}
          saving={saving}
          error={error}
          onSave={(body) => onSave(dxBodyToCapturedUpdate(body, {
            diagnosis:     editing.diagnosis,
            diagnosisCode: editing.diagnosisCode,
          }))}
          onClose={onClose}
          themeClass="is-update"
          menuClassName="ait-menu"
        />
      );
    }
    return wrap(
      <DiagnosisForm
        key={captured.id}
        formId={captured.id}
        initialPayload={captured.payload}
        saving={saving}
        error={error}
        onSave={(body) => onSave(dxBodyToCapturedAdd(body))}
        onClose={onClose}
        themeClass="is-manual"
        menuClassName="ait-menu"
        lockName
      />
    );
  }
  if (section !== "chief_complaints") return null;
  if (captured.operation === "update") {
    const p = captured.payload || {};
    const editing = {
      complaint:     p.complaint || "",
      complaintCode: p.icd_code ? { icdCode: p.icd_code } : null,
      complaintId:   captured.targetEntryId,
      current: {
        vas:             typeof p.vas_min === "number" ? p.vas_min : 0,
        sinceWhen:       p.since_when || "",
        description:     p.description || "",
        consultedDoctor: !!p.consulted_doctor,
      },
    };
    return wrap(
      <ComplaintForm
        key={captured.id}
        formId={captured.id}
        editing={editing}
        saving={saving}
        error={error}
        onSave={(body) => onSave(bodyToCapturedUpdate(body, {
          complaint:     editing.complaint,
          complaintCode: editing.complaintCode,
        }))}
        onClose={onClose}
        themeClass="is-update"
        menuClassName="ait-menu"
      />
    );
  }
  return wrap(
    <ComplaintForm
      key={captured.id}
      formId={captured.id}
      initialPayload={captured.payload}
      saving={saving}
      error={error}
      onSave={(body) => onSave(bodyToCapturedAdd(body))}
      onClose={onClose}
      themeClass="is-manual"
      menuClassName="ait-menu"
      lockComplaint
    />
  );
};
CapturedItemCard.propTypes = {
  captured:      PropTypes.object,
  section:       PropTypes.string.isRequired,
  patientId:     PropTypes.number,
  /** Currently-selected appointment (from AiTranscriptModal →
   *  ReviewSave). Forwarded onto section-specific forms that stamp
   *  it on the write body — Lab investigations reads it today. */
  appointmentId: PropTypes.number,
  saving:        PropTypes.bool,
  error:         PropTypes.string,
  onSave:        PropTypes.func.isRequired,
  onClose:       PropTypes.func.isRequired,
  /** View-only wrap: fieldset[disabled] + a "View only" ribbon.
      Used by the past-transcript reopen flow — the transcript is
      already committed, so the card exists to look, not edit. */
  readOnly:      PropTypes.bool,
};
CapturedItemCard.defaultProps = {
  captured:      null,
  patientId:     0,
  appointmentId: 0,
  saving:        false,
  error:         "",
  readOnly:      false,
};

const ReviewSave = ({
  items,
  turns,
  startedAt,
  endedAt,
  onClose,
  onFinish,
  onSaveSection,
  patient,
  patientId,
  appointmentId,
  transcriptId,
  transcriptService,
  version,
  onItemsSync,
  onVersionSync,
}) => {
  // Derive the ORDERED list of sections that need review (any state
  // except discarded). Order matches SECTION_TABS order in data.js so
  // the walk-through is stable across renders.
  const sectionsInOrder = useMemo(() => {
    const seen = new Set();
    for (const it of items) {
      if (!it.targetSection) continue;
      if (it.state === "discarded_in_call" || it.state === "discarded_in_review") continue;
      seen.add(it.targetSection);
    }
    return Array.from(seen);
  }, [items]);

  const [idx, setIdx] = useState(0);
  /* How many sections have been committed with Save & next, and therefore how
     far forward the tabs may be clicked. `idx` alone cannot answer that — it
     moves backwards too, the moment someone revisits an earlier section.
     A high-water mark, never decremented: re-saving a section already behind
     you must not re-lock the ones after it. */
  const [completed, setCompleted] = useState(0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  // Per-item failure map for the currently-active section. Populated
  // from the SaveSection response's `results[]`. Cleared on section
  // change / successful full-section save. Drives the row-level red
  // border + inline error banner + Retry / Skip-and-continue buttons.
  const [failuresByCapturedId, setFailuresByCapturedId] = useState({});
  const hasFailures = Object.keys(failuresByCapturedId).length > 0;
  // Click-to-edit on the review table. Only ONE card open at a time —
  // second click on another row swaps the card.
  const [editingCapturedId, setEditingCapturedId] = useState("");
  const [savingEdit, setSavingEdit] = useState(false);
  const [editError, setEditError] = useState("");
  // Drug schedule uses DrugScheduleEditor's own sheet stack (rather
  // than CapturedItemCard) — track its live sheet count so the layout
  // knows to spread has-card even while editingCapturedId is empty
  // (the reviewer hasn't clicked a row yet but a sheet is up).
  const [dsSheetCount, setDsSheetCount] = useState(0);
  useEffect(() => {
    const onCount = (payload) => setDsSheetCount((payload && payload.count) || 0);
    EventService.addListener(DS_V2_SHEET_COUNT, onCount);
    return () => EventService.removeListener(DS_V2_SHEET_COUNT, onCount);
  }, []);
  // Reject-entry flow. `rejectTarget` holds the captured item queued
  // for the "Reject this entry?" confirmation; `rejecting` blocks the
  // dialog buttons + row action while the decideCaptured request is
  // in flight.
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejecting, setRejecting] = useState(false);

  // Per-field rejections for patient_details. Client-side only: the
  // reviewer toggles a field × → the row strikes through, but the
  // server payload is NOT mutated until Save & finish. At that point
  // we send ONE decideCaptured per item with the mutated payload
  // (rejected fields stripped) before the SaveSection RPC fires, so
  // the Go writePatientDetails handler ends up committing only the
  // survivors. Structure: Map<capturedId, Set<fieldKey>>.
  const [patientDetailsRejected, setPatientDetailsRejected] = useState(() => new Map());
  // Confirmation gate for per-field REJECT (add to set). Restore (remove
  // from set) skips the dialog — reverting a mistake shouldn't cost a
  // click. Shape: { itemId, fieldKey, fieldLabel } or null.
  const [pdFieldRejectTarget, setPdFieldRejectTarget] = useState(null);
  const applyPatientDetailsFieldToggle = (itemId, fieldKey) => {
    setPatientDetailsRejected((prev) => {
      const next = new Map(prev);
      const set = new Set(next.get(itemId) || []);
      if (set.has(fieldKey)) set.delete(fieldKey);
      else set.add(fieldKey);
      if (set.size === 0) next.delete(itemId);
      else next.set(itemId, set);
      return next;
    });
  };
  const togglePatientDetailsFieldReject = (item, fieldKey) => {
    const already = (patientDetailsRejected.get(item.id) || new Set()).has(fieldKey);
    if (already) {
      // Restore — no confirmation needed.
      applyPatientDetailsFieldToggle(item.id, fieldKey);
      return;
    }
    setPdFieldRejectTarget({
      itemId: item.id,
      fieldKey,
      fieldLabel: PATIENT_DETAILS_FIELD_LABEL[fieldKey] || fieldKey,
    });
  };
  const confirmPatientDetailsFieldReject = () => {
    if (!pdFieldRejectTarget) return;
    applyPatientDetailsFieldToggle(pdFieldRejectTarget.itemId, pdFieldRejectTarget.fieldKey);
    setPdFieldRejectTarget(null);
  };

  const applyMutation = (resp) => {
    if (!resp || !resp.transcript) return;
    if (Array.isArray(resp.transcript.captured) && onItemsSync) {
      onItemsSync(resp.transcript.captured);
    }
    if (typeof resp.transcript.version === "number" && onVersionSync) {
      onVersionSync(resp.transcript.version);
    }
  };

  // Reject-entry handler. Marks the captured item as
  // `discarded_in_review` so Save & next skips it, but keeps it in
  // the review table (strikethrough + no × button) as an audit trail.
  const runReject = () => {
    const item = rejectTarget;
    if (!item || !transcriptService || !patientId || !transcriptId) {
      setRejectTarget(null);
      return;
    }
    setRejecting(true);
    transcriptService.decideCaptured(
      patientId, transcriptId, item.id,
      { newState: "discarded_in_review", expectedVersion: version || 0 },
      (resp) => {
        applyMutation(resp);
        setRejecting(false);
        setRejectTarget(null);
        // Clear the edit card if the reviewer was editing the row we
        // just rejected — nothing to save now.
        if (editingCapturedId === item.id) setEditingCapturedId("");
      },
      (err) => {
        setRejecting(false);
        setError((err && err.message) || "Could not reject entry. Try again.");
      },
    );
  };

  // Best-effort display name for the Reject dialog copy. Peeks at the
  // captured payload's usual name-carrying fields — the same ones the
  // per-section table renders in the first column.
  const rejectDisplayName = (item) => {
    const p = (item && item.payload) || {};
    // Order matches the per-section table's first column so the dialog
    // echoes the same label the reviewer saw when they clicked ×:
    //   CC → complaint · Dx → diagnosis · Med → medicine · Alg →
    //   substance · Hosp / FH → name · LI → test_name · Addictions →
    //   key (title-cased) · Anthropometric → parameter (via
    //   PARAMETER_BY_KEY) · fall back to p.value only when none of
    //   the above match. p.value for addictions is the combined
    //   "Key — status" adapter string, not the row's title, so the
    //   `addictionKey` shim sits ahead of it. Anthropometric's
    //   p.value is the numeric reading (e.g. `4'0"`), so its
    //   parameter-label shim sits ahead too.
    const addictionKey = p.key
      ? p.key.charAt(0).toUpperCase() + p.key.slice(1)
      : "";
    const anthroLabel = p.parameter && PARAMETER_BY_KEY[p.parameter]
      ? PARAMETER_BY_KEY[p.parameter].label
      : "";
    return (
      p.complaint || p.diagnosis || p.medicine || p.name || p.substance
      || p.test_name || addictionKey || anthroLabel || p.value
      || "this entry"
    );
  };

  // Save on the review-side edit card. Updates the captured item's
  // payload in place — state stays edited_in_call until Save & next
  // moves the whole section to reviewed_and_saved.
  const saveEdit = (captured, payload) => {
    if (!transcriptService || !patientId || !transcriptId) return;
    setSavingEdit(true);
    setEditError("");
    transcriptService.decideCaptured(
      patientId, transcriptId, captured.id,
      { newState: "edited_in_call", payload, expectedVersion: version || 0 },
      (resp) => {
        applyMutation(resp);
        setSavingEdit(false);
        setEditingCapturedId("");
      },
      (err) => {
        setSavingEdit(false);
        setEditError((err && err.message) || "Could not save. Try again.");
      },
    );
  };
  const cancelEdit = () => {
    setEditingCapturedId("");
    setEditError("");
  };

  if (sectionsInOrder.length === 0) {
    // Guard — shouldn't happen (button is disabled in the summary
    // dialog when total is 0) but keeps the render safe.
    return (
      <div className="ait-rev-shell" role="dialog" aria-modal="true" aria-label="Review & save">
        <div className="ait-rev-empty">Nothing to review. Close this and start a new call.</div>
      </div>
    );
  }

  const currentSection = sectionsInOrder[idx];
  // Rejected (discarded_in_review) items STAY in the table so the
  // reviewer sees an audit trail, sorted to the bottom with a
  // strikethrough visual treatment. Only discarded_in_call is fully
  // hidden — those were dropped during the call before the review
  // stage. Save & next still ignores both (see the loop above that
  // filters `state !== discarded_*`).
  const sectionItems = items
    .filter((it) => it.targetSection === currentSection
      && it.state !== "discarded_in_call")
    .slice()
    .sort((a, b) => {
      const ar = a.state === "discarded_in_review" ? 1 : 0;
      const br = b.state === "discarded_in_review" ? 1 : 0;
      return ar - br;
    });
  const isLast = idx === sectionsInOrder.length - 1;

  // Shared save-and-advance path. `skipOnFailure=false` aborts on the
  // first failure; `true` continues past failures and marks them
  // skipped_after_failure server-side (so the reviewer can come back).
  const runSave = async (skipOnFailure) => {
    setSaving(true);
    setError("");
    setEditingCapturedId("");
    try {
      // Patient details: apply per-field rejections to the server
      // payload BEFORE SaveSection reads it. For each item with any
      // rejections, POST decideCaptured with the mutated payload
      // (rejected fields stripped). If EVERY modified field for an
      // item is rejected, transition the item to discarded_in_review
      // so SaveSection skips it entirely and the section is a no-op.
      if (currentSection === "patient_details" && patientDetailsRejected.size > 0) {
        for (const it of sectionItems) {
          const rejects = patientDetailsRejected.get(it.id);
          if (!rejects || rejects.size === 0) continue;
          // Terminal states can't move — the server rejects any
          // transition. discarded_in_review is already terminal; the
          // reviewer rejecting a sub-field on top of it is a no-op.
          // reviewed_and_saved / discarded_in_call: same story.
          if (it.state === "discarded_in_review"
              || it.state === "discarded_in_call"
              || it.state === "reviewed_and_saved") continue;
          const modifiedRows = patientDetailsModifiedRows(it.payload || {}, patient || {});
          const survivingKeys = modifiedRows
            .map((r) => r.key)
            .filter((k) => !rejects.has(k));
          if (survivingKeys.length === 0) {
            // Nothing left to commit — discard the whole item.
            await new Promise((resolve, reject) => {
              transcriptService.decideCaptured(
                patientId, transcriptId, it.id,
                { newState: "discarded_in_review", expectedVersion: version || 0 },
                (resp) => { applyMutation(resp); resolve(); },
                (err) => reject(err),
              );
            });
          } else {
            // Rewrite the payload so only survivors remain. The Go
            // state machine forbids added_in_call → added_in_call, so
            // the target state depends on where we're coming from:
            //   proposed / added_in_call / edited_in_call → edited_in_call
            //   skipped_after_failure                     → reviewed_and_saved
            //                                              (only legal
            //                                              non-terminal move)
            // Same pattern as saveManualCardPatient in CapturedSections.
            let newState;
            if (it.state === "skipped_after_failure") {
              // Nothing legal here that preserves the review queue —
              // best we can do is drop the rewritten payload as an
              // upcoming reviewed_and_saved; SaveSection is a no-op
              // for already-reviewed items so nothing is written.
              // Skip the mutation instead.
              continue;
            } else if (it.state === "proposed"
                       || it.state === "added_in_call"
                       || it.state === "edited_in_call") {
              newState = "edited_in_call";
            } else {
              // Unknown/unexpected state — skip rather than 400.
              continue;
            }
            const nextPayload = {};
            for (const k of survivingKeys) nextPayload[k] = (it.payload || {})[k];
            // eslint-disable-next-line no-console
            console.log("[patient_details reject] decideCaptured", {
              itemId: it.id,
              fromState: it.state,
              newState,
              survivingKeys,
              rejectedKeys: Array.from(rejects),
              expectedVersion: version || 0,
            });
            await new Promise((resolve, reject) => {
              transcriptService.decideCaptured(
                patientId, transcriptId, it.id,
                {
                  newState,
                  payload: nextPayload,
                  expectedVersion: version || 0,
                },
                (resp) => { applyMutation(resp); resolve(); },
                (err) => reject(err),
              );
            });
          }
        }
      }

      const resp = await onSaveSection(currentSection, skipOnFailure);
      const results = Array.isArray(resp && resp.results) ? resp.results : [];
      const failed = results.filter((r) => r && r.success === false);
      if (failed.length > 0 && !skipOnFailure) {
        // Stay on this section, surface per-item errors.
        const map = {};
        failed.forEach((r) => { map[r.capturedId] = r.error || "Save failed"; });
        setFailuresByCapturedId(map);
        setSaving(false);
        return;
      }
      // Clean slate for the next section.
      setFailuresByCapturedId({});
      setCompleted((c) => Math.max(c, idx + 1));
      if (isLast) {
        onFinish();
      } else {
        setIdx(idx + 1);
      }
    } catch (e) {
      setError((e && e.message) || "Could not save this section. Try again.");
    } finally {
      setSaving(false);
    }
  };
  const onClickSaveNext = () => runSave(false);
  const onClickRetry    = () => runSave(false);
  const onClickSkip     = () => runSave(true);

  const heading = SECTION_LABEL[currentSection] || currentSection;

  return (
    <div className="ait-rev-shell" role="dialog" aria-modal="true" aria-label="Review & save">
      <header className="ait-rev-head">
        <span className="ait-rev-icon-tile" aria-hidden="true">
          <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor"
               strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M9 12l2 2 4-4" />
            <rect x="3" y="4" width="18" height="16" rx="2" />
          </svg>
        </span>
        <div className="ait-rev-titles">
          <h2 className="ait-rev-title">Review &amp; save</h2>
          <p className="ait-rev-sub">{formatStamp(startedAt || endedAt)}</p>
        </div>
        <button type="button" className="ait-rev-close" aria-label="Close" onClick={onClose}>
          &times;
        </button>
      </header>

      {/* SEQUENTIAL. A section opens only once every section before it has been
          committed with Save & next — so `i <= completed`, where completed is
          the number saved so far. Going BACK is always allowed: the review is
          ordered, not one-way, and someone who spots a mistake two sections ago
          must be able to return to it.

          `disabled`, not a click that silently does nothing: a locked tab has
          to look locked, and the title says why. */}
      <div className="ait-rev-tabs" role="tablist">
        {sectionsInOrder.map((sec, i) => {
          const locked = i > completed;
          return (
            <button
              key={sec}
              type="button"
              role="tab"
              aria-selected={i === idx}
              className={`ait-rev-tab${i === idx ? " is-active" : ""}${locked ? " is-locked" : ""}`}
              onClick={() => {
                setIdx(i);
                setEditingCapturedId("");
                setFailuresByCapturedId({});
              }}
              disabled={saving || locked}
              title={
                locked
                  ? `Save & next on ${SECTION_LABEL[sectionsInOrder[completed]] ||
                      sectionsInOrder[completed]} first`
                  : undefined
              }
            >
              {SECTION_LABEL[sec] || sec}
            </button>
          );
        })}
      </div>

      <div className="ait-rev-body">
        {/* Left rail — transcript replay, frozen "Ended" state. */}
        <aside className="ait-rev-rail">
          <div className="ait-rev-rail-status">
            <span className="ait-pill is-ended"><span />ENDED {durationLabel(startedAt, endedAt)}</span>
          </div>
          <p className="ait-rev-rail-eyebrow">Live transcript</p>
          <div className="ait-rev-chat">
            {turns.length === 0 ? (
              <p className="ait-rev-chat-empty">No transcript recorded.</p>
            ) : turns.map((t) => (
              <div className={`ait-turn is-${speakerCls(t.speaker)}`} key={t.id}>
                <div className="ait-turn-head">
                  <span className="ait-turn-speaker">{speakerLabel(t.speaker)}</span>
                </div>
                <p className="ait-bubble">{t.text}</p>
              </div>
            ))}
          </div>
        </aside>

        {/* Middle + right — active section's table on the left, and
            the click-to-edit card on the right when a status chip is
            clicked. Only Chief Complaints has a real editor today;
            other sections render the generic table read-only. */}
        <div className={`ait-rev-tblwrap${(editingCapturedId || (currentSection === "drug_schedule" && dsSheetCount > 0)) ? " has-card" : ""}`}>
          <div className="ait-rev-table-col">
            {currentSection === "chief_complaints" ? (
              <ChiefComplaintTable
                items={sectionItems}
                editingId={editingCapturedId}
                failures={failuresByCapturedId}
                onEdit={(it) => {
                  // Switching mid-edit swaps the card. Cancelling on
                  // the card clears editingCapturedId.
                  setEditingCapturedId(it.id);
                  setEditError("");
                }}
                onReject={(it) => setRejectTarget(it)}
              />
            ) : currentSection === "diagnosis" ? (
              <DiagnosisTable
                items={sectionItems}
                editingId={editingCapturedId}
                failures={failuresByCapturedId}
                onEdit={(it) => {
                  setEditingCapturedId(it.id);
                  setEditError("");
                }}
            onReject={(it) => setRejectTarget(it)}
              />
            ) : currentSection === "medicines" ? (
              <MedicineTable
                items={sectionItems}
                editingId={editingCapturedId}
                failures={failuresByCapturedId}
                patientId={patientId}
                onEdit={(it) => {
                  setEditingCapturedId(it.id);
                  setEditError("");
                }}
            onReject={(it) => setRejectTarget(it)}
              />
            ) : currentSection === "hospitalisation" ? (
              <HospitalisationTable
                items={sectionItems}
                editingId={editingCapturedId}
                failures={failuresByCapturedId}
                onEdit={(it) => {
                  setEditingCapturedId(it.id);
                  setEditError("");
                }}
            onReject={(it) => setRejectTarget(it)}
              />
            ) : currentSection === "lab_investigations" ? (
              <LabInvestigationTable
                items={sectionItems}
                editingId={editingCapturedId}
                failures={failuresByCapturedId}
                onEdit={(it) => {
                  setEditingCapturedId(it.id);
                  setEditError("");
                }}
            onReject={(it) => setRejectTarget(it)}
              />
            ) : currentSection === "allergies" ? (
              <AllergyTable
                items={sectionItems}
                editingId={editingCapturedId}
                failures={failuresByCapturedId}
                onEdit={(it) => {
                  setEditingCapturedId(it.id);
                  setEditError("");
                }}
            onReject={(it) => setRejectTarget(it)}
              />
            ) : currentSection === "family_history" ? (
              <FamilyHistoryTable
                items={sectionItems}
                editingId={editingCapturedId}
                failures={failuresByCapturedId}
                onEdit={(it) => {
                  setEditingCapturedId(it.id);
                  setEditError("");
                }}
            onReject={(it) => setRejectTarget(it)}
              />
            ) : currentSection === "addictions" ? (
              <AddictionTable
                items={sectionItems}
                editingId={editingCapturedId}
                failures={failuresByCapturedId}
                onEdit={(it) => {
                  setEditingCapturedId(it.id);
                  setEditError("");
                }}
            onReject={(it) => setRejectTarget(it)}
              />
            ) : currentSection === "activity" ? (
              <ActivityTable
                items={sectionItems}
                editingId={editingCapturedId}
                failures={failuresByCapturedId}
                onEdit={(it) => {
                  setEditingCapturedId(it.id);
                  setEditError("");
                }}
            onReject={(it) => setRejectTarget(it)}
              />
            ) : currentSection === "anthropometric" ? (
              <AnthropometricTable
                items={sectionItems}
                editingId={editingCapturedId}
                failures={failuresByCapturedId}
                onEdit={(it) => {
                  setEditingCapturedId(it.id);
                  setEditError("");
                }}
            onReject={(it) => setRejectTarget(it)}
              />
            ) : currentSection === "daily_schedule" ? (
              <DailyScheduleTable
                items={sectionItems}
                editingId={editingCapturedId}
                failures={failuresByCapturedId}
                onEdit={(it) => {
                  setEditingCapturedId(it.id);
                  setEditError("");
                }}
            onReject={(it) => setRejectTarget(it)}
              />
            ) : currentSection === "food_preferences" ? (
              <FoodPreferenceTable
                items={sectionItems}
                editingId={editingCapturedId}
                failures={failuresByCapturedId}
                onEdit={(it) => {
                  setEditingCapturedId(it.id);
                  setEditError("");
                }}
            onReject={(it) => setRejectTarget(it)}
              />
            ) : currentSection === "drug_schedule" ? (
              <DrugScheduleTable
                items={sectionItems}
                editingId={editingCapturedId}
                failures={failuresByCapturedId}
                onEdit={(it) => {
                  // Drug schedule doesn't use CapturedItemCard — the
                  // right column mounts DrugScheduleEditor instead.
                  // dsStagedAddToPlan handles the atom-expansion case
                  // (AI captures without a doses[] array) so the sheet
                  // opens pre-filled even for a proposal that hasn't
                  // been decided yet.
                  const plan = dsStagedAddToPlan(it);
                  setEditingCapturedId(it.id);
                  setEditError("");
                  EventService.emit(DS_V2_EDIT_CAPTURED, { plan, patient });
                }}
                onReject={(it) => setRejectTarget(it)}
              />
            ) : currentSection === "patient_details" ? (
              /* Single-record UPDATE section — one captured item,
                 rendered as one row per MODIFIED field. Per-field ×
                 marks the row as rejected (strike-through, client
                 state only). At Save & finish the parent iterates
                 the rejected set and rewrites the item's server
                 payload via decideCaptured BEFORE the SaveSection
                 RPC fires, so the Go writePatientDetails handler
                 commits only the survivors. If ALL modified fields
                 get rejected, the whole item transitions to
                 discarded_in_review and the section is skipped. */
              <PatientDetailsTable
                items={sectionItems}
                editingId={editingCapturedId}
                failures={failuresByCapturedId}
                onEdit={(it) => {
                  setEditingCapturedId(it.id);
                  setEditError("");
                }}
                onReject={(it) => setRejectTarget(it)}
                patient={patient}
                rejectedFields={patientDetailsRejected}
                onRejectField={togglePatientDetailsFieldReject}
              />
            ) : (
              <GenericTable items={sectionItems} heading={heading} />
            )}
            {error ? <p className="ait-rev-err">{error}</p> : null}
            {hasFailures ? (
              <div className="ait-rev-fail-banner">
                <p className="ait-rev-fail-title">
                  {Object.keys(failuresByCapturedId).length} of {sectionItems.length}
                  {" "}entries couldn’t be saved
                </p>
                <p className="ait-rev-fail-body">
                  Fix the highlighted rows above and click Retry — or Skip and
                  continue to move on. Skipped entries stay on the transcript
                  and can be revisited later.
                </p>
              </div>
            ) : null}
          </div>

          {currentSection === "drug_schedule" ? (
            /* Drug schedule reuses the same DrugScheduleEditor rail
               used during the call — clicking the DRUG cell fires
               DS_V2_EDIT_CAPTURED, the editor's SheetStack pushes
               an AddSheet pre-filled from the captured payload, and
               onCapturedUpdate ships the new payload through
               decideCaptured with newState=edited_in_call (same
               transition CapturedSections uses live). CapturedItemCard
               is skipped for this section — the rail is its editor. */
            <div className="ait-rev-card-col">
              <DrugScheduleEditor
                patient={patient}
                onCapturedUpdate={(capturedId, body, cbs) => {
                  const payload = drugScheduleBodyToCapturedAdd(body);
                  if (!transcriptService || !patientId || !transcriptId) {
                    if (cbs && cbs.onError) cbs.onError(new Error("transcript unavailable"));
                    return;
                  }
                  transcriptService.decideCaptured(
                    patientId, transcriptId, capturedId,
                    { newState: "edited_in_call", payload, expectedVersion: version || 0 },
                    (resp) => {
                      applyMutation(resp);
                      setEditingCapturedId("");
                      if (cbs && cbs.onSuccess) cbs.onSuccess();
                    },
                    (err) => {
                      if (cbs && cbs.onError) cbs.onError(err);
                    },
                  );
                }}
              />
            </div>
          ) : editingCapturedId ? (
            <div className="ait-rev-card-col">
              {(() => {
                const captured = sectionItems.find((it) => it.id === editingCapturedId);
                if (!captured) return null;
                return (
                  <CapturedItemCard
                    captured={captured}
                    section={currentSection}
                    patientId={patientId}
                    appointmentId={appointmentId}
                    saving={savingEdit}
                    error={editError}
                    onSave={(payload) => saveEdit(captured, payload)}
                    onClose={cancelEdit}
                  />
                );
              })()}
            </div>
          ) : null}
        </div>
      </div>
      <footer className="ait-rev-footer">
        {hasFailures ? (
          <>
            <button
              type="button"
              className="ait-rev-cta ait-rev-cta-ghost"
              onClick={onClickSkip}
              disabled={saving}
              title="Mark failed entries as skipped and move on. They stay on the transcript for later."
            >
              {saving ? "Saving…" : "Skip and continue"}
            </button>
            <button
              type="button"
              className="ait-rev-cta"
              onClick={onClickRetry}
              disabled={saving}
            >
              {saving ? "Retrying…" : "Retry"}
            </button>
          </>
        ) : (
          <button
            type="button"
            className="ait-rev-cta"
            onClick={onClickSaveNext}
            disabled={saving}
          >
            {saving ? "Saving…" : isLast ? "Save & finish" : "Save & next"}
          </button>
        )}
      </footer>
      {rejectTarget ? (
        <RejectConfirmDialog
          name={rejectDisplayName(rejectTarget)}
          submitting={rejecting}
          onCancel={() => setRejectTarget(null)}
          onConfirm={runReject}
        />
      ) : null}
      {pdFieldRejectTarget ? (
        <RejectConfirmDialog
          name={pdFieldRejectTarget.fieldLabel}
          onCancel={() => setPdFieldRejectTarget(null)}
          onConfirm={confirmPatientDetailsFieldReject}
        />
      ) : null}
    </div>
  );
};

ReviewSave.propTypes = {
  items:         PropTypes.array.isRequired,
  turns:         PropTypes.array.isRequired,
  startedAt:     PropTypes.string,
  endedAt:       PropTypes.string,
  onClose:       PropTypes.func.isRequired,
  onFinish:      PropTypes.func.isRequired,
  /** Async — resolves on server ack (POST /section/:section/save). */
  onSaveSection: PropTypes.func.isRequired,
  /** Current patient record — used by the patient_details table to
   *  filter its captured payload against the current values (only
   *  fields that actually changed are shown). */
  patient:       PropTypes.object,
  /** All of the following are only used by the click-to-edit save path. */
  patientId:         PropTypes.number,
  transcriptId:      PropTypes.string,
  transcriptService: PropTypes.object,
  version:           PropTypes.number,
  onItemsSync:       PropTypes.func,
  onVersionSync:     PropTypes.func,
};
ReviewSave.defaultProps = {
  startedAt: "",
  endedAt: "",
  patient: null,
  patientId: 0,
  transcriptId: "",
  transcriptService: null,
  version: 0,
  onItemsSync: () => {},
  onVersionSync: () => {},
};

export default ReviewSave;
